import { Col, Row, notification, InputNumber, Radio, Slider, Popconfirm, Spin, Form, Tooltip } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useCampaignPermissions } from "../../utils/permissions";
import { ArrowLeftOutlined, ArrowUpOutlined, EditOutlined, DeleteOutlined, PlusOutlined, FileTextOutlined, UploadOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import MobHeader from "../../Components/Header/MobHeader";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";
import {
  ThemeInput,
  ThemeForm,
  XThemeSelect,
  ThemeMainWrapper,
  ThemeFilterBtn,
  ThemeCard,
  ThemeTitle,
  ThemePara1,
  ThemeModal,
  ThemeSwitch,
} from "../Styles/Theme";
import {
  HeaderSection,
  HeaderLeft,
  BackButton,
  FormTitle,
  FormContent,
  ActionButtonsWrapper,
  UpdateButton,
  CancelButton,
  StyledInputNumber,
  StudyTypeRadioGroup,
  IncomeInputWrapper,
  AgeRangeHeader,
  AgeSliderWrapper,
  TickMarksRuler,
  TickMark,
  FieldLabelWithQuota,
  NestedQuotaSection,
  FeasibilitySection,
  FeasibilityResultCard,
  ZipcodeUploadArea,
  FeotSection,
} from "./CreateCampaignStyle";
import AdvancedTargetingSection from "./AdvancedTargetingSection";
import QuotaModal from "./QuotaModal";
import NestedQuotaModal from "./NestedQuotaModal";
import {
  createCampaignApi,
  createCampaignReset,
  updateCampaignApi,
  updateCampaignReset,
  getCampaignByIdApi,
  getCampaignByIdReset,
} from "../../store/actions/campaignAction";
import {
  getPollingAgentsApi,
  getPollingAgentsReset,
} from "../../store/actions/pollingAgentAction";
import {
  getSurveyDetailApi,
  getSurveyDetailReset,
} from "../../store/actions/surveyAction";
import {
  getCampaignTemplatesApi,
  getCampaignTemplatesReset,
  getCampaignTemplateByIdApi,
  getCampaignTemplateByIdReset,
  saveCampaignTemplateApi,
  saveCampaignTemplateReset,
} from "../../store/actions/campaignTemplateAction";

// Default survey message for new studies
const DEFAULT_SURVEY_MESSAGE = `<p>Thank you for participating in this important research project. Please note that everything shared is confidential, all viewpoints are respected, and there are no right or wrong answers. This is an open conversation that should not last more than 10 minutes.</p>
<p>On the next page, you will see phone numbers for different cities and a PIN number. Call the number for your city and enter the PIN (by voice) to begin the interview. Please click 'Next' to start the interview.</p>`;

const CreateCampaign = (props) => {
  const {
    createCampaign,
    createCampaignReset,
    createCampaignState,
    updateCampaign,
    updateCampaignReset,
    updateCampaignState,
    getCampaignById,
    getCampaignByIdReset,
    getCampaignByIdState,
    getPollingAgents,
    getPollingAgentsReset,
    getPollingAgentsState,
    getSurveyDetail,
    getSurveyDetailReset,
    getSurveyDetailState,
    getCampaignTemplates,
    getCampaignTemplatesReset,
    getCampaignTemplatesState,
    getCampaignTemplateById,
    getCampaignTemplateByIdReset,
    getCampaignTemplateByIdState,
    saveCampaignTemplate,
    saveCampaignTemplateReset,
    saveCampaignTemplateState,
  } = props;

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Folder context from URL params (for create-inside-folder support)
  const searchParams = new URLSearchParams(window.location.search);
  const createInFolderId = searchParams.get('folderId');
  const createInFolderName = searchParams.get('folderName');

  // Helper to build back URL with folder context
  const getBackUrl = () => {
    if (createInFolderId) {
      return `/campaigns/?folderId=${createInFolderId}&folderName=${encodeURIComponent(createInFolderName || '')}`;
    }
    return '/campaigns/';
  };

  // Permission check - shortcut hook (must be before any conditional returns)
  const { can } = useCampaignPermissions();

  // Check permissions for study types (same permissions used in CampaignDetail.js for showing cards)
  const canUsePureSpectrum = can('purespectrum');
  const canUseEmailInvite = can('email-invites');
  const canUseCPI = can('cpi');
  const canUseIncidence = can('incidence');
  const canCheckFeasibility = can('check-feasibility');
  const canUseFieldEvenly = can('field-evenly');
  // Dynamic column sizing: 3 base fields (Responses, Length, Field Time) + 0/1/2 extra (CPI, Incidence)
  const _extraFieldCount = (canUseCPI ? 1 : 0) + (canUseIncidence ? 1 : 0);
  const _firstColXl = _extraFieldCount === 2 ? 4 : _extraFieldCount === 1 ? 6 : 8;
  const _otherColXl = _extraFieldCount === 2 ? 5 : _extraFieldCount === 1 ? 6 : 8;

  // Determine available study types based on permissions
  const availableStudyTypes = [];
  if (canUsePureSpectrum) availableStudyTypes.push('purespectrum');
  if (canUseEmailInvite) availableStudyTypes.push('email-invite');

  // Determine default study type based on available permissions
  const getDefaultStudyType = () => {
    if (availableStudyTypes.length === 1) return availableStudyTypes[0];
    if (canUsePureSpectrum) return 'purespectrum';
    if (canUseEmailInvite) return 'email-invite';
    return 'purespectrum'; // fallback
  };

  const [form] = ThemeForm.useForm();
  const responsesValue = Form.useWatch('responses', form);
  const durationValue = Form.useWatch('duration', form);
  const [fieldEvenly, setFieldEvenly] = useState(false);
  const [feotIsCustom, setFeotIsCustom] = useState(false);
  const [feotPacing, setFeotPacing] = useState(null);
  const [feotInterval, setFeotInterval] = useState(null);
  // Watch quota-eligible fields for reactive quota button enable/disable
  const watchedFields = {
    gender: Form.useWatch('gender', form),
    children: Form.useWatch('children', form),
    hispanic_origin: Form.useWatch('hispanic_origin', form),
    education: Form.useWatch('education', form),
    relationship: Form.useWatch('relationship', form),
    device: Form.useWatch('device', form),
    employment: Form.useWatch('employment', form),
    race: Form.useWatch('race', form),
    income_min: Form.useWatch('income_min', form),
    income_max: Form.useWatch('income_max', form),
    region: Form.useWatch('region', form),
    division: Form.useWatch('division', form),
    state: Form.useWatch('state', form),
    msa: Form.useWatch('msa', form),
    csa: Form.useWatch('csa', form),
    county: Form.useWatch('county', form),
    zipcode: Form.useWatch('zipcode', form),
    dma: Form.useWatch('dma', form),
  };
  const [pollingAgents, setPollingAgents] = useState([]);
  const [pollingAgentsData, setPollingAgentsData] = useState([]); // Store full agent objects
  // Initialize with default message for new studies, empty for edit mode (will be loaded from API)
  const defaultMsg = isEdit ? '' : DEFAULT_SURVEY_MESSAGE;
  const [surveyMessage, setSurveyMessage] = useState(defaultMsg);
  const surveyMessageRef = useRef(defaultMsg);
  const editorRef = useRef(null); // TinyMCE editor instance ref
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [studyType, setStudyType] = useState(getDefaultStudyType());
  const [demographicOptions, setDemographicOptions] = useState({
    gender: [],
    race: [],
    region: [],
    division: [],
    state: [],
    msa: [],
    csa: [],
    county: [],
    zipcode: [],
    dma: [],
    education: [],
    employment: [],
    children: [],
    relationship: [],
    hispanic_origin: [],
    device: []
  });
  const [censusInfo, setCensusInfo] = useState({});
  const isEditDisabled = isEdit && (campaignStatus === 'completed' || campaignStatus === 'closed');
  const [locationFilter, setLocationFilter] = useState('none');
  const [ageSliderValue, setAgeSliderValue] = useState([18, 99]);
  const [advancedTargeting, setAdvancedTargeting] = useState([]);

  // Quota state
  const [quotas, setQuotas] = useState([]);
  const [quotaModalVisible, setQuotaModalVisible] = useState(false);
  const [quotaModalField, setQuotaModalField] = useState(null);
  const [quotaModalAtName, setQuotaModalAtName] = useState(null);
  // Nested quota state
  const [nestedQuotas, setNestedQuotas] = useState([]);
  const [nestedQuotaModalVisible, setNestedQuotaModalVisible] = useState(false);
  const [editingNestIndex, setEditingNestIndex] = useState(null);
  const [demographicsLoaded, setDemographicsLoaded] = useState(false);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [feasibilityResult, setFeasibilityResult] = useState(null);
  const [feasibilityError, setFeasibilityError] = useState(null);
  const [feasibilityStale, setFeasibilityStale] = useState(false);
  // Template state
  const [saveTemplateModalVisible, setSaveTemplateModalVisible] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [loadedTemplateId, setLoadedTemplateId] = useState(null); // tracks loaded template
  const [formDirtyAfterLoad, setFormDirtyAfterLoad] = useState(true); // true = no template loaded or form modified
  const isLoadingTemplateRef = useRef(false); // gates dirty detection during programmatic template load

  // Zipcode CSV upload state
  const [zipcodeFileName, setZipcodeFileName] = useState(null);
  const [zipcodePreviewOpen, setZipcodePreviewOpen] = useState(false);
  const zipcodeFileInputRef = useRef(null);

  // Watch form fields to reactively enable/disable feasibility button
  const watchCampaignName = ThemeForm.useWatch('campaign_name', form);
  const watchPollingAgentId = Form.useWatch('polling_agent_id', form);
  const watchResponses = ThemeForm.useWatch('responses', form);
  const watchSurveyDuration = ThemeForm.useWatch('survey_duration', form);
  const watchDuration = ThemeForm.useWatch('duration', form);

  // Can save as template: key template fields must be filled (study name excluded - not part of template)
  const mandatoryFieldsFilled = !!(
    watchPollingAgentId &&
    (watchedFields.gender && watchedFields.gender.length > 0)
  );
  const isTemplateUnchanged = loadedTemplateId && !formDirtyAfterLoad;
  const canSaveAsTemplate = mandatoryFieldsFilled && !isTemplateUnchanged;

  // Tooltip text for disabled save button
  const saveTemplateTooltip = !mandatoryFieldsFilled
    ? 'Fill required fields (AI JDV Agent, Gender) to save as template'
    : isTemplateUnchanged
    ? 'This data is already saved as a template'
    : '';

  const agentIdFromUrl = searchParams.get('agent');
  const surveyIdFromUrl = searchParams.get('survey');

  // Connected flow - coming from AI JDV with pre-filled data
  const isConnectedFlow = !!agentIdFromUrl;

  // Track which fields are pre-filled in connected flow (for selective disabling)
  const [preFilledFields, setPreFilledFields] = useState({
    campaign_name: false,
    polling_agent_id: false,
    topic: false,
    language: false,
    country: false,
    age_min: false,
    age_max: false,
    gender: false,
    race: false,
    state: false,
    dma: false,
    responses: false,
  });

  // Helper function to check if a field should be disabled
  // NOTE: To re-enable read-only mode for edit/connected flow, set ENABLE_FIELD_RESTRICTIONS to true
  const ENABLE_FIELD_RESTRICTIONS = false;

  // Check if study is live (in_progress, completed, paused, closed)
  // 'saved', 'ready', 'published' are NOT live - fields should still be editable
  const isLiveStudy = isEdit && campaignStatus && !['saved', 'ready', 'published'].includes(campaignStatus);

  const isFieldDisabled = (fieldName) => {
    // Closed study - disable all
    if (isEditDisabled) return true;

    // Study Type - disabled in edit mode
    if (fieldName === 'study_type') return isEdit;

    // AI JDV Agent - disabled in edit mode OR connected flow
    if (fieldName === 'polling_agent_id') return isEdit || isConnectedFlow;

    // For live PureSpectrum studies, these fields are read-only
    // (cannot be changed once the study is actually running)
    if (isLiveStudy && studyType === 'purespectrum') {
      const psLiveReadOnlyFields = ['country', 'language', 'survey_duration', 'qualifying_rate'];
      if (psLiveReadOnlyFields.includes(fieldName)) return true;
    }

    // Feature flag: When disabled, all other fields are editable
    if (!ENABLE_FIELD_RESTRICTIONS) return false;

    // === Below code only runs when ENABLE_FIELD_RESTRICTIONS is true ===
    // Study Name - always enabled (except closed study)
    if (fieldName === 'campaign_name') return false;

    // Survey Message - always enabled (except closed study)
    if (fieldName === 'survey_message') return false;

    // CPI - always enabled for purespectrum studies (except closed study)
    if (fieldName === 'cpi') return false;

    // Edit mode - disable all target audience fields
    if (isEdit) return true;

    // Connected flow - only disable pre-filled fields
    if (isConnectedFlow) return preFilledFields[fieldName] || false;

    // Create mode (normal) - enable all
    return false;
  };

  // Helper function to check if a field should be required
  const isFieldRequired = (fieldName) => {
    // If field is disabled, it's not required (skip validation)
    if (isFieldDisabled(fieldName)) return false;

    // Default required fields
    const requiredFields = ['campaign_name', 'polling_agent_id', 'language', 'country', 'age_min', 'age_max', 'gender', 'responses', 'survey_message'];
    return requiredFields.includes(fieldName);
  };

  // Handle age slider change
  const handleAgeSliderChange = (values) => {
    setAgeSliderValue(values);
    form.setFieldsValue({
      age_min: values[0],
      age_max: values[1],
    });
    clearQuotaForField('age');
  };

  // Handle agent selection - pre-fill duration from agent's estimated_duration
  const handleAgentChange = (agentId) => {
    if (!agentId || !pollingAgentsData || pollingAgentsData.length === 0) return;
    const selectedAgent = pollingAgentsData.find(agent => agent._id === agentId);
    if (selectedAgent && selectedAgent.estimated_duration) {
      form.setFieldsValue({ survey_duration: Math.min(selectedAgent.estimated_duration, 60) });
    }
  };

  // Check feasibility with PureSpectrum
  const handleCheckFeasibility = async () => {
    setFeasibilityLoading(true);
    setFeasibilityResult(null);
    setFeasibilityError(null);
    setFeasibilityStale(false);

    try {
      const values = form.getFieldsValue();

      const payload = {
        campaign_name: values.campaign_name || 'Feasibility Check',
        country: values.country || 'US',
        language: values.language || 'English',
        responses: values.responses,
        survey_duration: values.survey_duration,
        duration: values.duration,
        incidence: values.qualifying_rate,
        age_min: values.age_min ?? ageSliderValue[0],
        age_max: values.age_max ?? ageSliderValue[1],
        gender: values.gender || [],
        race: values.race || [],
        state: values.state || [],
        dma: values.dma || [],
        region: values.region || [],
        division: values.division || [],
        msa: values.msa || [],
        csa: values.csa || [],
        county: values.county || [],
        zipcode: values.zipcode || [],
        income_min: values.income_min != null
          ? Number(String(values.income_min).replace(/,/g, ''))
          : null,
        income_max: values.income_max != null
          ? Number(String(values.income_max).replace(/,/g, ''))
          : null,
        education: values.education || [],
        employment: values.employment || [],
        children: values.children || [],
        relationship: values.relationship || [],
        hispanic_origin: values.hispanic_origin || [],
        device: values.device || [],
        advanced_targeting: advancedTargeting.map(at => ({
          qualification_code: at.qualification_code,
          qualification_name: at.qualification_name,
          question_text: at.question_text,
          condition_codes: at.condition_codes,
          selected_labels: at.selected_labels,
        })),
        quotas: quotas
          .map(q => ({
            field: q.field,
            qualification_code: q.qualification_code,
            entries: q.entries
              .filter(e => e.count > 0)
              .map(e => ({
                condition_code: e.condition_code || null,
                label: e.label || null,
                range_from: e.range_from ?? null,
                range_to: e.range_to ?? null,
                units: e.units ?? null,
                count: e.count,
                type: e.type || null,
                gender: e.gender || null,
              })),
          }))
          .filter(q => q.entries.length > 0),
        nested_quotas: nestedQuotas
          .map(nest => ({
            group_key: nest.group_key,
            fields: nest.fields,
            cells: (nest.cells || [])
              .filter(c => c.required_count > 0)
              .map(c => ({
                quota_title: c.quota_title,
                required_count: c.required_count,
                criteria: c.criteria,
                is_combined: c.is_combined || false,
              })),
          }))
          .filter(nest => nest.cells.length > 0),
      };

      const response = await axios.post(
        `${config.api.base_url}/campaigns/purespectrum/feasibility`,
        payload,
        {
          headers: {
            Authorization: 'Bearer ' + getUserToken(),
          },
        }
      );

      if (response.data && response.data.status === 1) {
        setFeasibilityResult(response.data.data);
      } else {
        setFeasibilityError(response.data?.message || 'Feasibility check failed');
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Failed to check feasibility';
      setFeasibilityError(errorMsg);
      notification.error({
        message: 'Feasibility Check Failed',
        description: errorMsg,
      });
    } finally {
      setFeasibilityLoading(false);
    }
  };

  // Quota handlers
  const getQuotaForField = (field) => {
    return quotas.find(q => q.field === field);
  };

  const handleOpenQuotaModal = (field, atName = null) => {
    setQuotaModalField(field);
    setQuotaModalAtName(atName);
    setQuotaModalVisible(true);
  };

  const handleCloseQuotaModal = () => {
    setQuotaModalVisible(false);
    setQuotaModalField(null);
    setQuotaModalAtName(null);
  };

  const handleSaveQuota = (quotaData) => {
    setQuotas(prev => {
      const existing = prev.findIndex(q => q.field === quotaData.field);
      if (existing >= 0) {
        const newQuotas = [...prev];
        newQuotas[existing] = quotaData;
        return newQuotas;
      }
      return [...prev, quotaData];
    });
    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
    handleCloseQuotaModal();
  };

  const handleDeleteQuota = (field) => {
    setQuotas(prev => prev.filter(q => q.field !== field));
    cleanupNestedQuotasForField(field);
    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
  };

  // Clear quota when field selections change (options added/removed/cleared)
  const clearQuotaForField = (field) => {
    const existing = quotas.find(q => q.field === field);
    if (existing) {
      setQuotas(prev => prev.filter(q => q.field !== field));
      cleanupNestedQuotasForField(field);
    }
  };

  // Largest Remainder Method: distributes total across entries by percentages
  // Ensures sum always equals total exactly (Math.round can over/undershoot by up to +6)
  const largestRemainderMethod = (percentages, total) => {
    const values = percentages.map(p => {
      const exact = total * p / 100;
      return { floor: Math.floor(exact), remainder: exact - Math.floor(exact) };
    });
    const floorSum = values.reduce((sum, v) => sum + v.floor, 0);
    let remaining = total - floorSum;
    const indices = values.map((_, i) => i).sort((a, b) => values[b].remainder - values[a].remainder);
    const result = values.map(v => v.floor);
    for (let i = 0; i < remaining; i++) {
      result[indices[i]]++;
    }
    return result;
  };

  // One-click Census: auto-selects options + creates quota from census data
  const handleCensusClick = (field) => {
    const census = censusInfo[field];
    if (!census?.enabled || !census.data?.length || !responsesValue || responsesValue <= 0) return;

    const censusEntries = census.data.filter(d => d.rep_value > 0);
    if (censusEntries.length === 0) return;

    const distributed = largestRemainderMethod(
      censusEntries.map(e => e.rep_value),
      responsesValue
    );

    const isRangeField = field === 'age' || field === 'income';

    if (isRangeField) {
      const units = field === 'age' ? 311 : 321;
      const quotaEntries = censusEntries.map((entry, i) => ({
        range_from: entry.min,
        range_to: entry.max,
        units,
        label: field === 'income'
          ? `$${entry.min?.toLocaleString()} - $${entry.max?.toLocaleString()}`
          : `${entry.min} - ${entry.max}`,
        count: distributed[i],
      })).filter(e => e.count > 0);

      if (field === 'age') {
        const minAge = Math.min(...censusEntries.map(e => e.min));
        const maxAge = Math.max(...censusEntries.map(e => e.max));
        setAgeSliderValue([minAge, maxAge]);
        form.setFieldsValue({ age_min: minAge, age_max: maxAge });
      }
      if (field === 'income') {
        const minIncome = Math.min(...censusEntries.map(e => e.min));
        const maxIncome = Math.max(...censusEntries.map(e => e.max));
        form.setFieldsValue({ income_min: minIncome, income_max: maxIncome });
      }

      setQuotas(prev => {
        const existing = prev.findIndex(q => q.field === field);
        const quotaData = { field, qualification_code: census.qualification_code, entries: quotaEntries };
        if (existing >= 0) {
          const newQuotas = [...prev];
          newQuotas[existing] = quotaData;
          return newQuotas;
        }
        return [...prev, quotaData];
      });
    } else {
      // condition_code fields: auto-select options + create quota
      const fieldOptions = demographicOptions[field] || [];
      const selectedValues = [];
      const quotaEntries = [];

      censusEntries.forEach((entry, i) => {
        const option = fieldOptions.find(opt => opt.condition_code === entry.condition_code);
        if (option && distributed[i] > 0) {
          selectedValues.push(option.value);
          quotaEntries.push({
            condition_code: entry.condition_code,
            label: option.label,
            count: distributed[i],
          });
        }
      });

      // Auto-select options in form (overrides current selection)
      form.setFieldsValue({ [field]: selectedValues });

      setQuotas(prev => {
        const existing = prev.findIndex(q => q.field === field);
        const quotaData = { field, qualification_code: census.qualification_code, entries: quotaEntries };
        if (existing >= 0) {
          const newQuotas = [...prev];
          newQuotas[existing] = quotaData;
          return newQuotas;
        }
        return [...prev, quotaData];
      });
    }

    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
  };

  // Zipcode CSV helpers
  const handleZipcodeDownloadTemplate = () => {
    const csvContent = '91945\n91950\n91977\n91978';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'zipcode_template_US.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleZipcodeCSVUpload = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      notification.error({ message: 'Please upload a .CSV file' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      // Skip header if first line looks like a header
      const startIdx = lines.length > 0 && /^[a-zA-Z]/.test(lines[0]) ? 1 : 0;
      const zipcodes = lines.slice(startIdx)
        .map(line => line.split(',')[0]?.trim()) // Take first column
        .filter(z => z && /^\d{3,10}$/.test(z)); // Only valid numeric zipcodes
      if (zipcodes.length === 0) {
        notification.error({ message: 'No valid zipcodes found in CSV' });
        return;
      }
      // Deduplicate
      const unique = [...new Set(zipcodes)];
      form.setFieldsValue({ zipcode: unique });
      setZipcodeFileName(file.name);
      clearQuotaForField('zipcode');
      notification.success({ message: `${unique.length} zipcodes loaded from CSV` });
    };
    reader.readAsText(file);
  };

  const handleZipcodeClear = () => {
    form.setFieldsValue({ zipcode: [] });
    setZipcodeFileName(null);
    setZipcodePreviewOpen(false);
    if (zipcodeFileInputRef.current) zipcodeFileInputRef.current.value = '';
    clearQuotaForField('zipcode');
  };

  // Auto-cleanup nested quotas when a field's individual quota is removed
  const cleanupNestedQuotasForField = (field) => {
    setNestedQuotas(prev => {
      return prev.filter(nest => !nest.fields.includes(field));
    });
  };

  // Clear all quotas (including nested) when responses value changes (after initial form load)
  // Skip during programmatic template load (responses is set alongside quotas)
  const prevResponsesRef = useRef();
  useEffect(() => {
    if (prevResponsesRef.current !== undefined && responsesValue !== prevResponsesRef.current && !isLoadingTemplateRef.current) {
      if (quotas.length > 0) setQuotas([]);
      if (nestedQuotas.length > 0) setNestedQuotas([]);
    }
    prevResponsesRef.current = responsesValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsesValue]);

  // Reactive cleanup: auto-remove nested quota when any of its fields loses its individual quota
  useEffect(() => {
    const quotaFields = new Set(quotas.map(q => q.field));
    setNestedQuotas(prev => {
      if (prev.length === 0) return prev;
      const updated = prev.filter(nest =>
        (nest.fields || []).every(f => quotaFields.has(f))
      );
      return updated.length !== prev.length ? updated : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotas]);

  // Nested quota handlers — single nesting group
  const handleOpenNestedQuotaModal = () => {
    // If nesting exists, open in edit mode (index 0); otherwise create mode (null)
    setEditingNestIndex(nestedQuotas.length > 0 ? 0 : null);
    setNestedQuotaModalVisible(true);
  };

  const handleCloseNestedQuotaModal = () => {
    setNestedQuotaModalVisible(false);
    setEditingNestIndex(null);
  };

  const handleSaveNestedQuota = (nestData) => {
    // Single nesting group — always replace the one entry
    setNestedQuotas([nestData]);
    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
    handleCloseNestedQuotaModal();
  };

  const handleDeleteNestedQuota = () => {
    setNestedQuotas([]);
    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
  };

  // Get total quota count for a field
  const getQuotaTotal = (field) => {
    const quota = getQuotaForField(field);
    if (!quota) return 0;
    return quota.entries.reduce((sum, e) => sum + (e.count || 0), 0);
  };

  // Build tooltip content showing quota entries summary
  const getQuotaSummaryTooltip = (field) => {
    const quota = getQuotaForField(field);
    if (!quota || !quota.entries?.length) return null;
    const total = quota.entries.length;
    const totalCount = quota.entries.reduce((s, e) => s + (e.count || 0), 0);
    const scrollStyle = {
      maxHeight: '240px', overflowY: 'auto', marginRight: '-6px', paddingRight: '10px',
      scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.25) transparent',
    };
    return (
      <div style={{ padding: '4px 2px', fontSize: '12px', lineHeight: '1.7' }}>
        <div style={total > 10 ? scrollStyle : undefined}>
          {quota.entries.map((e, i) => {
            const label = e.range_from != null && e.range_to != null
              ? `${e.range_from}-${e.range_to}`
              : (e.label || '—');
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span>{label}</span>
                <span style={{ fontWeight: 600 }}>{e.count}</span>
              </div>
            );
          })}
        </div>
        {total > 10 && (
          <>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>{total} entries total</span>
              <span style={{ fontWeight: 600 }}>{totalCount}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render field label with quota button (inline, minimal)
  const renderFieldLabelWithQuota = (label, field, required = false) => {
    const quota = getQuotaForField(field);
    const hasQuota = quota && quota.entries?.length > 0;
    const quotaTotal = hasQuota ? getQuotaTotal(field) : 0;

    // Check if field has selections AND responses is filled (required for quota)
    const canAddQuota = () => {
      if (!responsesValue || responsesValue <= 0) return false;

      if (field === 'age') {
        return true; // age always has default slider values (13-99)
      }
      if (field === 'income') {
        return watchedFields.income_min != null && watchedFields.income_max != null && watchedFields.income_min <= watchedFields.income_max;
      }
      const values = watchedFields[field];
      return Array.isArray(values) && values.length > 0;
    };

    return (
      <FieldLabelWithQuota>
        {label}
        {required && <span className="label-required">*</span>}
        {censusInfo[field]?.enabled && (() => {
          const noResponses = !responsesValue || responsesValue <= 0;
          const censusDisabled = isEditDisabled || noResponses || hasQuota;
          const tooltipTitle = noResponses ? 'Add responses first' : hasQuota ? 'Delete existing quota first' : '';
          const censusBtn = (
            <button
              type="button"
              className="census-btn"
              onClick={(e) => { e.stopPropagation(); handleCensusClick(field); }}
              disabled={censusDisabled}
            >
              Census
            </button>
          );
          return censusDisabled && tooltipTitle ? (
            <Tooltip title={tooltipTitle} placement="top">
              <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>{censusBtn}</span>
            </Tooltip>
          ) : censusBtn;
        })()}
        {hasQuota ? (
          <span className="quota-badge" style={!censusInfo[field]?.enabled ? { marginLeft: 'auto' } : undefined}>
            <Tooltip title={getQuotaSummaryTooltip(field)} placement="bottom">
              <InfoCircleOutlined className="quota-icon" style={{ color: '#3d2969', cursor: 'pointer' }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
            <span className="quota-text">Quota</span>
            <EditOutlined
              className="quota-icon edit-icon"
              onClick={(e) => { e.stopPropagation(); handleOpenQuotaModal(field); }}
            />
            <Popconfirm
              title="Remove quota?"
              description="This will remove the quota for this field."
              onConfirm={() => handleDeleteQuota(field)}
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <DeleteOutlined className="quota-icon delete-icon" onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
          </span>
        ) : (() => {
          const isDisabled = isEditDisabled || !canAddQuota();
          const tooltipMsg = !responsesValue || responsesValue <= 0
            ? 'Add responses first'
            : 'Select options first';
          const hasCensus = censusInfo[field]?.enabled;
          const btn = (
            <button
              type="button"
              className="add-quota-btn"
              style={!hasCensus ? { marginLeft: 'auto' } : undefined}
              onClick={(e) => { e.stopPropagation(); handleOpenQuotaModal(field); }}
              disabled={isDisabled}
            >
              <PlusOutlined /> Quota
            </button>
          );
          return isDisabled ? (
            <Tooltip title={tooltipMsg} placement="top">
              <span style={{ marginLeft: !hasCensus ? 'auto' : undefined, display: 'inline-flex' }}>{btn}</span>
            </Tooltip>
          ) : btn;
        })()}
      </FieldLabelWithQuota>
    );
  };

  // Render quota button for age field (inline, before age tag)
  const renderAgeQuotaButton = () => {
    const quota = getQuotaForField('age');
    const hasQuota = quota && quota.entries?.length > 0;
    const quotaTotal = hasQuota ? getQuotaTotal('age') : 0;
    const canAdd = responsesValue && responsesValue > 0;

    const censusEnabled = censusInfo.age?.enabled;
    const noResponses = !canAdd;
    const censusDisabled = isEditDisabled || noResponses || hasQuota;
    const censusTooltip = noResponses ? 'Add responses first' : hasQuota ? 'Delete existing quota first' : '';
    const ageBtnStyle = { fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 600, color: '#3d2969', background: 'rgba(61, 41, 105, 0.1)', border: 'none', padding: '2px 8px', borderRadius: '4px', marginRight: '8px' };

    const censusBtn = censusEnabled ? (
      censusDisabled ? (
        <Tooltip title={censusTooltip || 'Census unavailable'} placement="top">
          <span style={{ display: 'inline-flex', marginRight: '8px' }}>
            <button type="button" style={{ ...ageBtnStyle, cursor: 'not-allowed', opacity: 0.4, marginRight: 0 }} disabled>Census</button>
          </span>
        </Tooltip>
      ) : (
        <button type="button" style={{ ...ageBtnStyle, cursor: 'pointer' }} onClick={() => handleCensusClick('age')}>Census</button>
      )
    ) : null;

    if (hasQuota) {
      return (
        <>
          {censusBtn}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(61, 41, 105, 0.1)', padding: '2px 8px', borderRadius: '4px', marginRight: '8px' }}>
            <Tooltip title={getQuotaSummaryTooltip('age')} placement="bottom">
              <InfoCircleOutlined style={{ fontSize: '12px', color: '#3d2969', cursor: 'pointer' }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 600, color: '#3d2969' }}>Quota</span>
            <EditOutlined style={{ fontSize: '12px', color: '#3d2969', cursor: 'pointer' }} onClick={() => handleOpenQuotaModal('age')} />
            <Popconfirm
              title="Remove quota?"
              description="This will remove the quota for this field."
              onConfirm={() => handleDeleteQuota('age')}
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <DeleteOutlined style={{ fontSize: '12px', color: '#ff4d4f', cursor: 'pointer' }} />
            </Popconfirm>
          </span>
        </>
      );
    }

    const isDisabled = isEditDisabled || !canAdd;
    const btn = (
      <button
        type="button"
        style={{ ...ageBtnStyle, cursor: isDisabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px', opacity: isDisabled ? 0.4 : 1 }}
        onClick={() => handleOpenQuotaModal('age')}
        disabled={isDisabled}
      >
        <PlusOutlined style={{ fontSize: '10px' }} /> Quota
      </button>
    );

    if (isDisabled) {
      return (
        <>
          {censusBtn}
          <Tooltip title="Add responses first" placement="top">
            <span style={{ display: 'inline-flex', marginRight: '8px' }}>{React.cloneElement(btn, { style: { ...btn.props.style, marginRight: 0 } })}</span>
          </Tooltip>
        </>
      );
    }
    return <>{censusBtn}{btn}</>;
  };

  // Fetch campaign data if editing
  useEffect(() => {
    if (isEdit && id) {
      getCampaignById(id);
    }
  }, [id, isEdit, getCampaignById]);

  // Fetch survey data if survey parameter is present (for pre-filling form)
  useEffect(() => {
    if (!isEdit && surveyIdFromUrl) {
      getSurveyDetail(surveyIdFromUrl);
    }
    return () => {
      getSurveyDetailReset();
    };
  }, [surveyIdFromUrl, isEdit, getSurveyDetail, getSurveyDetailReset]);

  // Fetch polling agents for dropdown
  useEffect(() => {
    getPollingAgents({ page: 1, limit: 1000 });
  }, [getPollingAgents]);

  // Fetch demographic options from API
  useEffect(() => {
    const fetchDemographicOptions = async () => {
      try {
        const response = await axios.get(`${config.api.base_url}/campaigns/demographic-options`, {
          headers: {
            Authorization: 'Bearer ' + getUserToken(),
          },
        });
        if (response.data && response.data.status === 1) {
          const data = response.data.data;
          setDemographicOptions({
            gender: data.gender?.options || [],
            race: data.race?.options || [],
            region: data.region?.options || [],
            division: data.division?.options || [],
            state: data.state?.options || [],
            msa: data.msa?.options || [],
            csa: data.csa?.options || [],
            county: data.county?.options || [],
            zipcode: data.zipcode?.options || [],
            dma: data.dma?.options || [],
            education: data.education?.options || [],
            employment: data.employment?.options || [],
            children: data.children?.options || [],
            relationship: data.relationship?.options || [],
            hispanic_origin: data.hispanic_origin?.options || [],
            device: data.device?.options || []
          });
          // Extract census info per field
          const censusMap = {};
          Object.entries(data).forEach(([key, val]) => {
            if (val) {
              censusMap[key] = {
                enabled: val.census_enabled || false,
                data: val.census_data || [],
                qualification_code: val.qualification_code,
              };
            }
          });
          setCensusInfo(censusMap);
        }
      } catch (error) {
        console.error('Error fetching demographic options:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to load demographic options'
        });
      } finally {
        setDemographicsLoaded(true);
      }
    };
    fetchDemographicOptions();
  }, []);

  // Handle polling agents response
  useEffect(() => {
    if (getPollingAgentsState.apiState === 'success' && getPollingAgentsState.data) {
      // The response structure from reducer: action.response (which is response.data from axios)
      // So getPollingAgentsState.data is the response.data from axios
      // Which should have structure: { status, message, data: { polling_agents: [...], pagination: {...} } }
      const responseData = getPollingAgentsState.data;
      
      // Extract agents from the nested structure
      const agentsData = responseData.data || responseData;
      const agents = agentsData.polling_agents || agentsData.agents || [];
      
      if (agents && agents.length > 0) {
        const agentOptions = agents.map((agent) => ({
          value: agent._id,
          label: `${agent.agent_name || agent.name || '-'}`,
        }));
        setPollingAgents(agentOptions);
        setPollingAgentsData(agents); // Store full agent objects
      } else {
        setPollingAgents([]);
        setPollingAgentsData([]);
      }
      getPollingAgentsReset();
    } else if (getPollingAgentsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getPollingAgentsState.message || 'Failed to fetch AI JDV agents',
      });
      getPollingAgentsReset();
    }
  }, [getPollingAgentsState, getPollingAgentsReset]);

  // Handle survey data response for pre-filling form
  useEffect(() => {
    if (getSurveyDetailState.apiState === 'success' && getSurveyDetailState.data && !isEdit) {
      const responseData = getSurveyDetailState.data;
      const surveyData = responseData.data || responseData;
      const survey = surveyData.survey || surveyData;
      const demographics = survey?.demographics || {};

      const formValues = {
        campaign_name: survey?.title || '',
        topic: demographics.topic || '',
        country: demographics.country || 'US',
        age_min: demographics.ageRange?.min || 18,
        age_max: demographics.ageRange?.max || 99,
        language: demographics.language || null,
        gender: demographics.gender || [],
        race: demographics.race || [],
        region: demographics.region || [],
        division: demographics.division || [],
        state: demographics.state || [],
        msa: demographics.msa || [],
        csa: demographics.csa || [],
        county: demographics.county || [],
        zipcode: demographics.zipcode || [],
        dma: demographics.dma || [],
        responses: demographics.responsesTarget || null,
      };

      form.setFieldsValue(formValues);

      // Update age slider value
      setAgeSliderValue([
        demographics.ageRange?.min || 18,
        demographics.ageRange?.max || 99
      ]);
      // Track which fields are pre-filled (for selective disabling in connected flow)
      // A field is considered pre-filled if it has a meaningful value
      if (isConnectedFlow) {
        setPreFilledFields({
          campaign_name: !!survey?.title,
          polling_agent_id: !!agentIdFromUrl, // Always pre-filled in connected flow
          topic: !!demographics.topic,
          language: !!demographics.language,
          country: !!demographics.country,
          age_min: demographics.ageRange?.min != null,
          age_max: demographics.ageRange?.max != null,
          gender: (demographics.gender || []).length > 0,
          race: (demographics.race || []).length > 0,
          region: (demographics.region || []).length > 0,
          division: (demographics.division || []).length > 0,
          state: (demographics.state || []).length > 0,
          msa: (demographics.msa || []).length > 0,
          csa: (demographics.csa || []).length > 0,
          county: (demographics.county || []).length > 0,
          zipcode: (demographics.zipcode || []).length > 0,
          dma: (demographics.dma || []).length > 0,
          responses: demographics.responsesTarget != null,
        });
      }

      // Detect which location type is active
      const geoDetectSurvey = [
        ['region', demographics.region],
        ['division', demographics.division],
        ['state', demographics.state],
        ['msa', demographics.msa],
        ['csa', demographics.csa],
        ['county', demographics.county],
        ['zipcode', demographics.zipcode],
        ['dma', demographics.dma],
      ];
      const activeGeoSurvey = geoDetectSurvey.find(([, v]) => (v || []).length > 0);
      setLocationFilter(activeGeoSurvey ? activeGeoSurvey[0] : 'none');
      if ((demographics.zipcode || []).length > 0) setZipcodeFileName(null);

      getSurveyDetailReset();
    } else if (getSurveyDetailState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getSurveyDetailState.message || 'Failed to fetch survey data',
      });
      getSurveyDetailReset();
    }
  }, [getSurveyDetailState, getSurveyDetailReset, isEdit, form, isConnectedFlow, agentIdFromUrl]);

  // Auto-fill agent from URL parameter and set default country (only in create mode)
  useEffect(() => {
    if (!isEdit) {
      const defaultStudyType = getDefaultStudyType();
      const defaultValues = {
        country: 'US',
        study_type: defaultStudyType,
        cpi: 5,
      };

      if (agentIdFromUrl && pollingAgents.length > 0) {
        const agentExists = pollingAgents.find(agent => agent.value === agentIdFromUrl);
        if (agentExists) {
          defaultValues.polling_agent_id = agentIdFromUrl;
        }
      }

      form.setFieldsValue(defaultValues);
      setStudyType(defaultStudyType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentIdFromUrl, pollingAgents, isEdit, form, canUsePureSpectrum, canUseEmailInvite, availableStudyTypes.length]);

  // Pre-fill duration when agent data is loaded
  useEffect(() => {
    if (!isEdit && pollingAgentsData.length > 0) {
      const currentAgentId = form.getFieldValue('polling_agent_id');
      if (currentAgentId) {
        const selectedAgent = pollingAgentsData.find(agent => agent._id === currentAgentId);
        if (selectedAgent && selectedAgent.estimated_duration) {
          form.setFieldsValue({ survey_duration: Math.min(selectedAgent.estimated_duration, 60) });
        }
      }
    }
  }, [pollingAgentsData, isEdit, form]);

  // Handle campaign data response for edit
  useEffect(() => {
    if (getCampaignByIdState.apiState === 'success' && getCampaignByIdState.data && isEdit) {
      // Response structure: getCampaignByIdState.data is response.data from axios
      // Which should have structure: { status, message, data: campaign }
      const responseData = getCampaignByIdState.data;
      const campaign = responseData.data || responseData;

      setCampaignStatus(campaign.status);
      setStudyType(campaign.study_type || 'purespectrum');

      if (campaign.status === 'completed' || campaign.status === 'closed') {
        notification.warning({
          message: 'Study Closed',
          description: 'This study is closed and cannot be edited.',
          duration: 5,
        });
      }

      // Base form values (always rendered fields)
      const formValues = {
        campaign_name: campaign.campaign_name || '',
        study_type: campaign.study_type || 'purespectrum',
        topic: campaign.topic || '',
        polling_agent_id: campaign.polling_agent_id?._id || campaign.polling_agent_id || '',
        country: campaign.country || 'US',
        age_min: campaign.age_min || 18,
        age_max: campaign.age_max || 99,
        language: campaign.language || null,
        gender: campaign.gender || [],
        race: campaign.race || [],
        state: campaign.state || [],
        dma: campaign.dma || [],
        region: campaign.region || [],
        division: campaign.division || [],
        msa: campaign.msa || [],
        csa: campaign.csa || [],
        county: campaign.county || [],
        zipcode: campaign.zipcode || [],
        // Demographic fields
        income_min: campaign.income_min || null,
        income_max: campaign.income_max || null,
        education: campaign.education || [],
        employment: campaign.employment || [],
        children: campaign.children || [],
        relationship: campaign.relationship || [],
        hispanic_origin: campaign.hispanic_origin || [],
        device: campaign.device || [],
      };

      form.setFieldsValue(formValues);

      // PS-specific fields are conditionally rendered based on studyType state
      // Use setTimeout to allow React to re-render with new studyType before setting these values
      // Delay of 100ms ensures React has time to flush the state update and render the fields
      if (campaign.study_type === 'purespectrum') {
        setTimeout(() => {
          form.setFieldsValue({
            responses: campaign.responses ?? null,
            duration: campaign.duration ?? null,
            survey_duration: campaign.survey_duration ?? null,
            cpi: campaign.cpi ?? null,
            qualifying_rate: campaign.qualifying_rate ?? null,
          });
        }, 100);
        setFieldEvenly(campaign.field_evenly === true);
        setFeotIsCustom(campaign.feot_is_custom === true);
        setFeotPacing(campaign.feot_pacing || null);
        setFeotInterval(campaign.feot_interval || null);
      }

      // Update age slider value
      const editAgeMin = campaign.age_min || 18;
      const editAgeMax = campaign.age_max || 99;
      setAgeSliderValue([editAgeMin, editAgeMax]);
      // Detect which location type is active
      const geoDetect = [
        ['region', campaign.region],
        ['division', campaign.division],
        ['state', campaign.state],
        ['msa', campaign.msa],
        ['csa', campaign.csa],
        ['county', campaign.county],
        ['zipcode', campaign.zipcode],
        ['dma', campaign.dma],
      ];
      const activeGeo = geoDetect.find(([, v]) => (v || []).length > 0);
      setLocationFilter(activeGeo ? activeGeo[0] : 'none');

      // Set zipcode file name hint for edit mode
      if ((campaign.zipcode || []).length > 0) {
        setZipcodeFileName(null); // null shows "Loaded zipcodes" text
      }

      // Load Advanced Targeting
      if (campaign.advanced_targeting && Array.isArray(campaign.advanced_targeting)) {
        setAdvancedTargeting(campaign.advanced_targeting.map((at, idx) => ({
          ...at,
          id: at.id || `at_loaded_${idx}_${Date.now()}`,
        })));
      } else {
        setAdvancedTargeting([]);
      }

      // Load Quotas
      if (campaign.quotas && Array.isArray(campaign.quotas)) {
        setQuotas(campaign.quotas);
      } else {
        setQuotas([]);
      }

      // Load Nested Quotas
      if (campaign.nested_quotas && Array.isArray(campaign.nested_quotas)) {
        setNestedQuotas(campaign.nested_quotas);
      } else {
        setNestedQuotas([]);
      }

      const surveyMsg = campaign.survey_message || '';
      setSurveyMessage(surveyMsg);
      surveyMessageRef.current = surveyMsg;
      form.setFieldsValue({
        survey_message: surveyMsg,
      });
      // Set TinyMCE editor content if editor is already initialized
      if (editorRef.current) {
        editorRef.current.setContent(surveyMsg);
      }
      getCampaignByIdReset();
    } else if (getCampaignByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCampaignByIdState.message || 'Failed to fetch campaign',
      });
      getCampaignByIdReset();
      navigate(getBackUrl());
    }
  }, [getCampaignByIdState, isEdit, getCampaignByIdReset, navigate, form]);

  // Handle create response
  useEffect(() => {
    if (createCampaignState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: createCampaignState.message || 'Study created successfully',
      });
      const campaignData = createCampaignState.data;
      const newCampaignId = campaignData?.data?._id || campaignData?._id || campaignData?.id || campaignData?.campaign?._id;
      // Fire-and-forget: add new campaign to folder if created inside a folder
      if (createInFolderId && newCampaignId) {
        axios.post(
          `${config.api.base_url}/folders/${createInFolderId}/add-item`,
          { itemId: newCampaignId },
          { headers: { Authorization: 'Bearer ' + getUserToken() } }
        ).catch(() => {});
      }
      createCampaignReset();
      if (newCampaignId) {
        let detailUrl = `/campaigns/${newCampaignId}`;
        if (createInFolderId) {
          detailUrl += `?fromFolderId=${createInFolderId}&fromFolderName=${encodeURIComponent(createInFolderName || '')}`;
        }
        navigate(detailUrl);
      } else {
        navigate(getBackUrl());
      }
    } else if (createCampaignState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createCampaignState.message || 'Failed to create campaign',
      });
      createCampaignReset();
    }
  }, [createCampaignState, createCampaignReset, navigate]);

  // Handle update response
  useEffect(() => {
    if (updateCampaignState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: updateCampaignState.message || 'Study updated successfully',
      });
      updateCampaignReset();
      if (id) {
        let detailUrl = `/campaigns/${id}`;
        if (createInFolderId) {
          detailUrl += `?fromFolderId=${createInFolderId}&fromFolderName=${encodeURIComponent(createInFolderName || '')}`;
        }
        navigate(detailUrl);
      } else {
        navigate(getBackUrl());
      }
    } else if (updateCampaignState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateCampaignState.message || 'Failed to update campaign',
      });
      updateCampaignReset();
    }
  }, [updateCampaignState, updateCampaignReset, navigate, id]);

  // ============== Template Feature ==============

  // Fetch templates on mount (create mode only, with permission)
  useEffect(() => {
    if (!isEdit && (can('load-template') || can('save-template'))) {
      getCampaignTemplates();
    }
    return () => {
      getCampaignTemplatesReset();
      getCampaignTemplateByIdReset();
      saveCampaignTemplateReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle save template response
  useEffect(() => {
    if (saveCampaignTemplateState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Template saved successfully',
      });
      setSaveTemplateModalVisible(false);
      setTemplateName('');
      saveCampaignTemplateReset();
      getCampaignTemplates();
    } else if (saveCampaignTemplateState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: saveCampaignTemplateState.message || 'Failed to save template',
      });
      saveCampaignTemplateReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCampaignTemplateState.apiState]);

  // Handle load template response - pre-fill form (mirrors edit-mode pattern)
  useEffect(() => {
    if (getCampaignTemplateByIdState.apiState === 'success' && getCampaignTemplateByIdState.data) {
      const responseData = getCampaignTemplateByIdState.data;
      const template = responseData.data || responseData;
      const td = template.form_data;

      if (!td) {
        notification.warning({ message: 'Warning', description: 'Template has no form data' });
        getCampaignTemplateByIdReset();
        return;
      }

      // Gate dirty detection during programmatic template load
      isLoadingTemplateRef.current = true;

      // Set study type first (controls conditional field rendering)
      setStudyType(td.study_type || 'purespectrum');

      // Base form values (always-rendered fields)
      // responses/duration/survey_duration set here (not in setTimeout) so the quota cleanup
      // useEffect sees isLoadingTemplateRef.current = true and skips clearing quotas
      const formValues = {
        study_type: td.study_type || 'purespectrum',
        topic: td.topic || '',
        polling_agent_id: td.polling_agent_id || '',
        country: td.country || 'US',
        age_min: td.age_min || 18,
        age_max: td.age_max || 99,
        language: td.language || null,
        gender: td.gender || [],
        race: td.race || [],
        state: td.state || [],
        dma: td.dma || [],
        region: td.region || [],
        division: td.division || [],
        msa: td.msa || [],
        csa: td.csa || [],
        county: td.county || [],
        zipcode: td.zipcode || [],
        responses: td.responses ?? null,
        duration: td.duration ?? null,
        survey_duration: td.survey_duration ?? null,
      };

      form.setFieldsValue(formValues);

      // PS-specific demographic fields with setTimeout (ensures conditional fields have mounted after studyType change)
      if (td.study_type === 'purespectrum') {
        setTimeout(() => {
          form.setFieldsValue({
            income_min: td.income_min ?? null,
            income_max: td.income_max ?? null,
            education: td.education || [],
            employment: td.employment || [],
            children: td.children || [],
            relationship: td.relationship || [],
            hispanic_origin: td.hispanic_origin || [],
            device: td.device || [],
            cpi: td.cpi ?? null,
            qualifying_rate: td.qualifying_rate ?? null,
          });
          isLoadingTemplateRef.current = false;
        }, 100);
        setFieldEvenly(td.field_evenly === true);
        setFeotIsCustom(td.feot_is_custom === true);
        setFeotPacing(td.feot_pacing || null);
        setFeotInterval(td.feot_interval || null);
      } else {
        // Clear PS-specific fields to prevent stale values if user switches back to PS later
        setTimeout(() => {
          form.setFieldsValue({
            income_min: null,
            income_max: null,
            education: [],
            employment: [],
            children: [],
            relationship: [],
            hispanic_origin: [],
            device: [],
          });
          isLoadingTemplateRef.current = false;
        }, 100);
      }

      // Age slider
      setAgeSliderValue([td.age_min || 18, td.age_max || 99]);

      // Detect which location type is active
      const geoDetectTpl = [
        ['region', td.region],
        ['division', td.division],
        ['state', td.state],
        ['msa', td.msa],
        ['csa', td.csa],
        ['county', td.county],
        ['zipcode', td.zipcode],
        ['dma', td.dma],
      ];
      const activeGeoTpl = geoDetectTpl.find(([, v]) => (v || []).length > 0);
      setLocationFilter(activeGeoTpl ? activeGeoTpl[0] : 'none');

      // Reset zipcode file state for template load
      if ((td.zipcode || []).length > 0) {
        setZipcodeFileName(null);
      } else {
        setZipcodeFileName(null);
        if (zipcodeFileInputRef.current) zipcodeFileInputRef.current.value = '';
      }

      // Advanced targeting
      if (td.advanced_targeting && Array.isArray(td.advanced_targeting)) {
        setAdvancedTargeting(td.advanced_targeting.map((at, idx) => ({
          ...at,
          id: at.id || `at_tpl_${idx}_${Date.now()}`,
        })));
      } else {
        setAdvancedTargeting([]);
      }

      // Quotas
      if (td.quotas && Array.isArray(td.quotas)) {
        setQuotas(td.quotas);
      } else {
        setQuotas([]);
      }

      // Nested quotas
      if (td.nested_quotas && Array.isArray(td.nested_quotas)) {
        setNestedQuotas(td.nested_quotas);
      } else {
        setNestedQuotas([]);
      }

      // Survey message
      const surveyMsg = td.survey_message || '';
      setSurveyMessage(surveyMsg);
      surveyMessageRef.current = surveyMsg;
      form.setFieldsValue({ survey_message: surveyMsg });
      if (editorRef.current) {
        editorRef.current.setContent(surveyMsg);
      }

      // Track loaded template for dirty detection
      setLoadedTemplateId(template._id);
      setFormDirtyAfterLoad(false);

      getCampaignTemplateByIdReset();
      notification.success({ message: 'Template Loaded', description: 'Review and adjust fields as needed.' });
    } else if (getCampaignTemplateByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCampaignTemplateByIdState.message || 'Failed to load template',
      });
      getCampaignTemplateByIdReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCampaignTemplateByIdState.apiState]);

  // Save current form as template
  const handleSaveAsTemplate = () => {
    if (!templateName.trim()) return;

    const values = form.getFieldsValue();
    const surveyMessageContent = surveyMessageRef.current || '';

    const templateData = {
      study_type: studyType,
      topic: values.topic || null,
      country: values.country || 'US',
      age_min: values.age_min ?? ageSliderValue[0],
      age_max: values.age_max ?? ageSliderValue[1],
      language: values.language || null,
      gender: values.gender || [],
      race: values.race || [],
      state: values.state || [],
      dma: values.dma || [],
      region: values.region || [],
      division: values.division || [],
      msa: values.msa || [],
      csa: values.csa || [],
      county: values.county || [],
      zipcode: values.zipcode || [],
      polling_agent_id: values.polling_agent_id || null,
      survey_message: surveyMessageContent,
      income_min: values.income_min != null ? Number(String(values.income_min).replace(/,/g, '')) : null,
      income_max: values.income_max != null ? Number(String(values.income_max).replace(/,/g, '')) : null,
      education: values.education || [],
      employment: values.employment || [],
      children: values.children || [],
      relationship: values.relationship || [],
      hispanic_origin: values.hispanic_origin || [],
      device: values.device || [],
      advanced_targeting: advancedTargeting.map(at => ({
        qualification_code: at.qualification_code,
        qualification_name: at.qualification_name,
        question_text: at.question_text,
        condition_codes: at.condition_codes,
        selected_labels: at.selected_labels,
      })),
      quotas: quotas,
      nested_quotas: nestedQuotas,
      responses: values.responses ?? null,
      duration: values.duration ?? null,
      survey_duration: values.survey_duration ?? null,
      cpi: values.cpi ?? null,
      qualifying_rate: values.qualifying_rate ?? null,
      field_evenly: fieldEvenly,
      feot_is_custom: feotIsCustom,
      feot_pacing: feotPacing,
      feot_interval: feotInterval,
    };

    saveCampaignTemplate({
      template_name: templateName.trim(),
      form_data: templateData,
    });
  };

  const isSurveyMessageEmpty = (content) => {
    if (!content) return true;
    const stripped = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
    return stripped.length === 0;
  };

  const handleSubmit = (values) => {
    // Validate survey message using ref (since it's not managed by Form)
    if (isFieldRequired('survey_message') && isSurveyMessageEmpty(surveyMessageRef.current)) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter survey message',
      });
      return;
    }

    // Validate FEOT Custom mode requires interval
    if (fieldEvenly && feotIsCustom && !feotInterval) {
      notification.error({
        message: 'Validation Error',
        description: 'Please enter an interval value for Custom mode, or switch to Daily.',
      });
      return;
    }

    const surveyMessageContent = surveyMessageRef.current || '';

    const submitData = {
      campaign_name: values.campaign_name,
      topic: values.topic || null,
      country: values.country || 'US',
      age_min: values.age_min ?? null,
      age_max: values.age_max ?? null,
      language: values.language || null,
      gender: values.gender || [],
      race: values.race || [],
      state: values.state || [],
      dma: values.dma || [],
      region: values.region || [],
      division: values.division || [],
      msa: values.msa || [],
      csa: values.csa || [],
      county: values.county || [],
      zipcode: values.zipcode || [],
      polling_agent_id: values.polling_agent_id,
      survey_message: surveyMessageContent,
      status: 'saved',
    };

    if (studyType === 'purespectrum') {
      // PureSpectrum demographic fields
      submitData.income_min = values.income_min != null ? Number(String(values.income_min).replace(/,/g, '')) : null;
      submitData.income_max = values.income_max != null ? Number(String(values.income_max).replace(/,/g, '')) : null;
      submitData.education = values.education || [];
      submitData.employment = values.employment || [];
      submitData.children = values.children || [];
      submitData.relationship = values.relationship || [];
      submitData.hispanic_origin = values.hispanic_origin || [];
      submitData.device = values.device || [];
      // Advanced Targeting (Class 2)
      submitData.advanced_targeting = advancedTargeting.map(at => ({
        qualification_code: at.qualification_code,
        qualification_name: at.qualification_name,
        question_text: at.question_text,
        condition_codes: at.condition_codes,
        selected_labels: at.selected_labels,
      }));
      // Quotas - filter out orphaned (field emptied) and zero-count entries
      submitData.quotas = quotas
        .filter(q => {
          // Check the field still has values selected
          if (q.field === 'age') return values.age_min != null && values.age_max != null;
          if (q.field === 'income') return values.income_min != null || values.income_max != null;
          // AT fields: check the AT question still exists in advancedTargeting
          const atMatch = q.field.match(/^at_(\d+)$/);
          if (atMatch) {
            const qualCode = parseInt(atMatch[1], 10);
            return advancedTargeting.some(a => a.qualification_code === qualCode);
          }
          const fieldVal = values[q.field];
          return Array.isArray(fieldVal) && fieldVal.length > 0;
        })
        .map(q => ({
          field: q.field,
          qualification_code: q.qualification_code,
          entries: q.entries
            .filter(e => e.count > 0)
            .map(e => ({
              condition_code: e.condition_code || null,
              label: e.label || null,
              range_from: e.range_from ?? null,
              range_to: e.range_to ?? null,
              units: e.units ?? null,
              count: e.count,
              type: e.type || null,
              gender: e.gender || null,
            })),
        }))
        .filter(q => q.entries.length > 0);
      // Nested quotas - filter out cells with 0 count
      submitData.nested_quotas = nestedQuotas
        .map(nest => ({
          group_key: nest.group_key,
          fields: nest.fields,
          cells: (nest.cells || [])
            .filter(c => c.required_count > 0)
            .map(c => ({
              quota_title: c.quota_title,
              required_count: c.required_count,
              criteria: c.criteria,
              is_combined: c.is_combined || false,
              combined_label: c.combined_label || null,
              sub_cells: c.is_combined ? (c.sub_cells || []) : [],
            })),
        }))
        .filter(nest => nest.cells.length > 0);
      // PureSpectrum study fields
      submitData.responses = values.responses ?? null;
      submitData.cpi = values.cpi ?? 5;
      submitData.duration = values.duration ?? null;
      submitData.survey_duration = values.survey_duration ?? null;
      submitData.qualifying_rate = values.qualifying_rate ?? 50;
      submitData.field_evenly = fieldEvenly;
      submitData.feot_is_custom = feotIsCustom;
      submitData.feot_pacing = feotPacing;
      submitData.feot_interval = feotInterval;
    } else {
      // Clear PureSpectrum fields when not using PureSpectrum study type
      submitData.income_min = null;
      submitData.income_max = null;
      submitData.education = [];
      submitData.employment = [];
      submitData.children = [];
      submitData.relationship = [];
      submitData.hispanic_origin = [];
      submitData.device = [];
      submitData.advanced_targeting = [];
      submitData.quotas = [];
      submitData.nested_quotas = [];
      submitData.responses = null;
      submitData.cpi = null;
      submitData.duration = null;
      submitData.survey_duration = null;
      submitData.qualifying_rate = null;
      submitData.field_evenly = false;
      submitData.feot_is_custom = false;
      submitData.feot_pacing = null;
      submitData.feot_interval = null;
    }

    if (isEdit) {
      // study_type cannot be changed after creation
      updateCampaign(id, submitData);
    } else {
      // Include study_type only for create
      submitData.study_type = studyType;
      createCampaign(submitData);
    }
  };

  // Sample options for dropdowns - values are already capitalized/full names
  const countryOptions = [
    { label: 'United States', value: 'US' },
  ];

  const languageOptions = [
    { label: 'English', value: 'English' },
    { label: 'Spanish', value: 'Spanish' },
    { label: 'French', value: 'French' },
    { label: 'German', value: 'German' },
    { label: 'Chinese', value: 'Chinese' },
  ];

  // Permission check - redirect if no permission for create/edit (must be after all hooks)
  const requiredPermission = isEdit ? 'edit' : 'create';
  if (!can(requiredPermission)) {
    return <Navigate to={getBackUrl()} replace />;
  }

  // Form loading state - wait for initial data to load
  const isFormLoading = getPollingAgentsState.apiState === 'loading' || !demographicsLoaded || (isEdit && getCampaignByIdState.apiState === 'loading');

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper className="campaign-detail-wrapper">
        <HeaderSection>
          <HeaderLeft>
            {/* <BackButton
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/campaigns/')}
            /> */}
             <ThemeFilterBtn
                size="medium"
                icon={<img src="/images/back-icon.svg" alt="Back" />}
                className="noBackground mr-2"
                onClick={() => navigate(getBackUrl())}
              />
            <FormTitle>{isEdit ? 'Edit Study' : 'Create Study'}</FormTitle>
          </HeaderLeft>
          {/* Load from template - only in create mode with permission */}
          {!isEdit && can('load-template') && (
            <XThemeSelect
              style={{ minWidth: 240 }}
              size="middle"
              placeholder="Load from template..."
              allowClear
              options={(getCampaignTemplatesState.data?.data?.templates || []).map(t => ({
                label: t.template_name,
                value: t._id,
              }))}
              loading={getCampaignTemplatesState.apiState === 'loading' || getCampaignTemplateByIdState.apiState === 'loading'}
              onChange={(templateId) => {
                if (templateId) {
                  getCampaignTemplateById(templateId);
                } else {
                  // User cleared the selection (X button) — reset form to fresh-page state
                  const defaultStudy = getDefaultStudyType();
                  form.resetFields();
                  // Re-apply same defaults as the initial page load useEffect
                  const resetValues = {
                    country: 'US',
                    study_type: defaultStudy,
                    age_min: 18,
                    age_max: 99,
                  };
                  // Preserve agent from URL if in connected flow
                  if (agentIdFromUrl && pollingAgents.length > 0) {
                    const agentExists = pollingAgents.find(a => a.value === agentIdFromUrl);
                    if (agentExists) resetValues.polling_agent_id = agentIdFromUrl;
                  }
                  form.setFieldsValue(resetValues);
                  setStudyType(defaultStudy);
                  setAgeSliderValue([18, 99]);
                  setLocationFilter('none');
                  setAdvancedTargeting([]);
                  setQuotas([]);
                  setNestedQuotas([]);
                  const defaultMsgContent = DEFAULT_SURVEY_MESSAGE;
                  setSurveyMessage(defaultMsgContent);
                  surveyMessageRef.current = defaultMsgContent;
                  if (editorRef.current) {
                    editorRef.current.setContent(defaultMsgContent);
                  }
                  setLoadedTemplateId(null);
                  setFormDirtyAfterLoad(true);
                  setFeasibilityResult(null);
                  setFeasibilityError(null);
                  setFeasibilityStale(false);
                }
              }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          )}
        </HeaderSection>

        <Spin spinning={isFormLoading} size="large">
        <FormContent>
            <ThemeForm
              form={form}
              name="campaignForm"
              layout="vertical"
              autoComplete="off"
              onFinish={handleSubmit}
              disabled={isEditDisabled}
              onValuesChange={(changedValues) => {
                if (feasibilityResult) setFeasibilityStale(true);
                // Only mark dirty for template-relevant fields (campaign_name is not part of template)
                // Skip during programmatic template load (setTimeout triggers onValuesChange)
                if (!isLoadingTemplateRef.current) {
                  const templateFields = Object.keys(changedValues).filter(k => k !== 'campaign_name');
                  if (templateFields.length > 0 && loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
                }
              }}
            >
              {/* Row 1: Study Name | AI JDV Agent | Study Type */}
              <Row gutter={[24, 0]}>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Study Name"
                    name="campaign_name"
                    rules={[{ required: isFieldRequired('campaign_name'), message: 'Please enter study name' }]}
                  >
                    <ThemeInput
                      size="large"
                      placeholder="Enter study name"
                      tabIndex={1}
                      disabled={isFieldDisabled('campaign_name')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="AI JDV Agent"
                    name="polling_agent_id"
                    rules={[{ required: isFieldRequired('polling_agent_id'), message: 'Please select AI JDV Agent' }]}
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      size="large"
                      placeholder="Select AI JDV Agent"
                      options={pollingAgents}
                      loading={getPollingAgentsState.apiState === 'loading'}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={handleAgentChange}
                      tabIndex={2}
                      disabled={isFieldDisabled('polling_agent_id')}
                    />
                  </ThemeForm.Item>
                </Col>
                {availableStudyTypes.length > 0 && (
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Study Type"
                    name="study_type"
                    rules={[{ required: !isFieldDisabled('study_type'), message: 'Please select study type' }]}
                    tooltip={isEdit ? "Study type cannot be changed after creation" : undefined}
                  >
                    <StudyTypeRadioGroup
                      value={studyType}
                      onChange={(e) => {
                        setStudyType(e.target.value);
                        if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
                      }}
                      disabled={isFieldDisabled('study_type')}
                    >
                      {canUsePureSpectrum && (
                        <Radio value="purespectrum">
                          <div className="radio-label-content">
                            <span className="radio-title">System Managed</span>
                          </div>
                          <div className="radio-icon">
                            <img src="/images/check-icon.svg" alt="Selected" />
                          </div>
                        </Radio>
                      )}
                      {canUseEmailInvite && (
                        <Radio value="email-invite">
                          <div className="radio-label-content">
                            <span className="radio-title">Custom Upload</span>
                          </div>
                          <div className="radio-icon">
                            <img src="/images/check-icon.svg" alt="Selected" />
                          </div>
                        </Radio>
                      )}
                    </StudyTypeRadioGroup>
                  </ThemeForm.Item>
                </Col>
                )}
              </Row>
              <Row className="my-2">
                <Col xs={24}>
                  <ThemeTitle className="smallTitle mb-2">Target Audience</ThemeTitle>
                </Col>
              </Row>
            <ThemeCard className="p-5 mb-5">
              {isConnectedFlow && (
                <ThemePara1 className="darkColor mb-4">Based on your agent's target profile, here's the audience I've pre-selected.</ThemePara1>
              )}
              {/* PS fields - Responses, CPI, Length, Incidence, Field Time */}
              {studyType === 'purespectrum' && (
              <>
              <Row gutter={[24, 0]}>
                <Col xs={12} md={8} xl={_firstColXl}>
                  <ThemeForm.Item
                    label="Responses"
                    name="responses"
                    tooltip="Number of completes needed for this study"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, message: 'Min 1' },
                      { type: 'number', max: 250000, message: 'Max 250,000' },
                    ]}
                  >
                    <StyledInputNumber
                      size="large"
                      placeholder="Enter"
                      min={1}
                      max={250000}
                      controls={false}
                      tabIndex={17}
                      disabled={isFieldDisabled('responses')}
                    />
                  </ThemeForm.Item>
                </Col>
                {canUseCPI && (
                <Col xs={12} md={8} xl={_otherColXl}>
                  <ThemeForm.Item
                    label="CPI"
                    tooltip="Cost per interview (USD, max $9,999)"
                  >
                    <IncomeInputWrapper>
                      <span className="income-prefix">$</span>
                      <ThemeForm.Item
                        name="cpi"
                        noStyle
                        rules={[
                          { type: 'number', max: 9999, message: 'Max $9,999' },
                        ]}
                      >
                        <InputNumber
                          placeholder="0.00"
                          min={0}
                          max={9999}
                          step={0.01}
                          precision={2}
                          tabIndex={18}
                          disabled={isFieldDisabled('cpi')}
                        />
                      </ThemeForm.Item>
                    </IncomeInputWrapper>
                  </ThemeForm.Item>
                </Col>
                )}
                <Col xs={12} md={8} xl={_otherColXl}>
                  <ThemeForm.Item
                    label="Length (mins)"
                    name="survey_duration"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, message: 'Min 1' },
                      { type: 'number', max: 60, message: 'Max 60 minutes' },
                    ]}
                    tooltip="Study duration in minutes (1-60)"
                  >
                    <StyledInputNumber
                      size="large"
                      placeholder="Enter"
                      min={1}
                      max={60}
                      controls={false}
                      tabIndex={19}
                      disabled={isFieldDisabled('survey_duration')}
                    />
                  </ThemeForm.Item>
                </Col>
                {canUseIncidence && (
                <Col xs={12} md={8} xl={_otherColXl}>
                  <ThemeForm.Item
                    label="Incidence (%)"
                    name="qualifying_rate"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, message: 'Min 1%' },
                      { type: 'number', max: 99, message: 'Max 99%' },
                    ]}
                    tooltip="Qualification rate (1-99%)"
                  >
                    <StyledInputNumber
                      size="large"
                      placeholder="Enter"
                      min={1}
                      max={99}
                      controls={false}
                      formatter={value => value ? `${value}%` : ''}
                      parser={value => value.replace('%', '')}
                      tabIndex={20}
                      disabled={isFieldDisabled('qualifying_rate')}
                    />
                  </ThemeForm.Item>
                </Col>
                )}
                <Col xs={12} md={8} xl={_otherColXl}>
                  <ThemeForm.Item
                    label="Field Time (days)"
                    name="duration"
                    tooltip="Number of days the study will be in field"
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, message: 'Min 1' },
                      { type: 'number', max: 999, message: 'Max 999 days' },
                    ]}
                  >
                    <StyledInputNumber
                      size="large"
                      placeholder="Enter"
                      min={1}
                      max={999}
                      controls={false}
                      tabIndex={21}
                      disabled={isFieldDisabled('duration')}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>
              {canUseFieldEvenly && (
              <Row gutter={[24, 0]} style={{ marginBottom: 0 }}>
                <Col xs={24}>
                  <FeotSection $expanded={fieldEvenly} $showCustom={feotIsCustom}>
                    <div className="feot-line">
                      <ThemeSwitch
                        size="small"
                        checked={fieldEvenly}
                        onChange={(checked) => {
                          setFieldEvenly(checked);
                          if (!checked) {
                            setFeotIsCustom(false);
                            setFeotPacing(null);
                            setFeotInterval(null);
                          }
                        }}
                        disabled={isFieldDisabled('duration')}
                      />
                      <span className="feot-label">Field Evenly Over Time</span>
                      <Tooltip title="FEOT does not guarantee a goal is met, but it limits the number of completes allowed each day for a quota to ensure that the maximum number of daily completes won't exceed the same amount for every day the project is in the field.">
                        <InfoCircleOutlined style={{ color: '#485066', fontSize: '12px' }} />
                      </Tooltip>
                      {fieldEvenly && (
                        <>
                          <div className="feot-divider" />
                          <span className="feot-toggle-label">Daily</span>
                          <ThemeSwitch
                            size="small"
                            checked={feotIsCustom}
                            onChange={(checked) => {
                              setFeotIsCustom(checked);
                              if (checked) {
                                setFeotPacing('days');
                                setFeotInterval(null);
                              } else {
                                setFeotPacing(null);
                                setFeotInterval(null);
                              }
                            }}
                            disabled={isFieldDisabled('duration')}
                          />
                          <span className="feot-toggle-label">Custom</span>
                        </>
                      )}
                      {fieldEvenly && responsesValue > 0 && durationValue > 0 && (
                        <>
                          <div className="feot-divider" />
                          <span className="feot-calc">
                            {(() => {
                              const remaining = feotIsCustom && feotPacing
                                ? (feotPacing === 'hours' ? durationValue * 24 : feotPacing === 'weeks' ? Math.ceil(durationValue / 7) : durationValue)
                                : durationValue;
                              const perUnit = Math.round(responsesValue / Math.max(1, remaining));
                              const unit = feotIsCustom && feotPacing
                                ? (feotPacing === 'hours' ? 'hour' : feotPacing === 'weeks' ? 'week' : 'day')
                                : 'day';
                              const units = feotIsCustom && feotPacing
                                ? (feotPacing === 'hours' ? 'hours' : feotPacing === 'weeks' ? 'weeks' : 'days')
                                : 'days';
                              return <><strong>{perUnit}</strong> {perUnit <= 1 ? 'complete' : 'completes'} per {unit} for <strong>{remaining}</strong> remaining {units} in field.</>;
                            })()}
                          </span>
                        </>
                      )}
                    </div>
                    {fieldEvenly && feotIsCustom && (
                      <div className="feot-custom-line">
                        <span className="feot-field-label">Time window:</span>
                        <Radio.Group
                          value={feotPacing}
                          onChange={(e) => { setFeotPacing(e.target.value); setFeotInterval(null); }}
                          disabled={isFieldDisabled('duration')}
                        >
                          <Radio value="hours">Hours</Radio>
                          <Radio value="days">Days</Radio>
                          <Radio value="weeks">Weeks</Radio>
                        </Radio.Group>
                        <div className="feot-divider" />
                        <span className="feot-field-label">Every</span>
                        <InputNumber
                          className="feot-interval-input"
                          size="small"
                          min={1}
                          max={Math.min(99, feotPacing === 'hours' ? (durationValue || 1) * 24 : feotPacing === 'weeks' ? Math.ceil((durationValue || 1) / 7) : durationValue || 1)}
                          value={feotInterval}
                          onChange={(val) => setFeotInterval(val)}
                          disabled={isFieldDisabled('duration')}
                          controls={false}
                        />
                        <span className="feot-field-hint">{feotPacing === 'hours' ? 'Hours' : feotPacing === 'weeks' ? 'Weeks' : 'Days'}</span>
                      </div>
                    )}
                  </FeotSection>
                </Col>
              </Row>
              )}
              </>
              )}

              <Row gutter={[24, 0]}>
              {/* Row 1: Topic | Language | Country */}
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Topic"
                    name="topic"
                  >
                    <ThemeInput
                      size="large"
                      placeholder="Enter study topic"
                      tabIndex={3}
                      disabled={isFieldDisabled('topic')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Language"
                    name="language"
                    initialValue="English"
                    rules={[{ required: isFieldRequired('language'), message: 'Please select language' }]}
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      size="large"
                      placeholder="Select language"
                      options={languageOptions}
                      tabIndex={4}
                      disabled={isFieldDisabled('language')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Country"
                    name="country"
                    rules={[{ required: isFieldRequired('country'), message: 'Please select country' }]}
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      size="large"
                      placeholder="Select country"
                      options={countryOptions}
                      tabIndex={5}
                      disabled={isFieldDisabled('country')}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>

              {/* Row 2: Age Range | Gender | Filter by Location (3 columns) */}
              <Row gutter={[24, 0]}>
                <Col xs={24} md={8}>
                  <ThemeForm.Item>
                    <AgeRangeHeader>
                      <span className="fieldLabel">Age Range</span>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {studyType === 'purespectrum' && renderAgeQuotaButton()}
                        <span className="ageValue">{ageSliderValue[0]} - {ageSliderValue[1]}</span>
                      </span>
                    </AgeRangeHeader>
                    <AgeSliderWrapper>
                      <Slider
                        range
                        min={13}
                        max={99}
                        value={ageSliderValue}
                        onChange={handleAgeSliderChange}
                        disabled={isFieldDisabled('age_min')}
                        tooltip={{ formatter: (value) => value }}
                      />
                      <TickMarksRuler>
                        {Array.from({ length: 87 }, (_, i) => {
                          const age = 13 + i;
                          const isMajor = age % 10 === 0;
                          return (
                            <TickMark key={age} $isMajor={isMajor}>
                              {isMajor && <span className="label">{age}</span>}
                            </TickMark>
                          );
                        })}
                      </TickMarksRuler>
                    </AgeSliderWrapper>
                    {/* Hidden fields to store form values */}
                    <ThemeForm.Item name="age_min" hidden>
                      <InputNumber />
                    </ThemeForm.Item>
                    <ThemeForm.Item name="age_max" hidden>
                      <InputNumber />
                    </ThemeForm.Item>
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={studyType === 'purespectrum' ? renderFieldLabelWithQuota('Gender', 'gender', isFieldRequired('gender')) : 'Gender'}
                    name="gender"
                    rules={[{ required: isFieldRequired('gender'), message: 'Please select gender' }]}
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Gender"
                      options={demographicOptions.gender}
                      tabIndex={7}
                      disabled={isFieldDisabled('gender')}
                      onChange={() => clearQuotaForField('gender')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label="Filter by Location"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      size="large"
                      value={locationFilter}
                      onChange={(value) => {
                        setLocationFilter(value);
                        form.setFieldsValue({ region: [], division: [], state: [], msa: [], csa: [], county: [], zipcode: [], dma: [] });
                        ['region', 'division', 'state', 'msa', 'csa', 'county', 'zipcode', 'dma'].forEach(f => clearQuotaForField(f));
                        setZipcodeFileName(null);
                        setZipcodePreviewOpen(false);
                        if (zipcodeFileInputRef.current) zipcodeFileInputRef.current.value = '';
                        if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
                      }}
                      disabled={['region', 'division', 'state', 'msa', 'csa', 'county', 'zipcode', 'dma'].some(f => isFieldDisabled(f))}
                      options={[
                        { label: 'None', value: 'none' },
                        { label: 'Region', value: 'region' },
                        { label: 'Division', value: 'division' },
                        { label: 'State', value: 'state' },
                        { label: 'CSA', value: 'csa' },
                        { label: 'MSA / CBSA', value: 'msa' },
                        { label: 'County', value: 'county' },
                        { label: 'DMA', value: 'dma' },
                        { label: 'Zipcode', value: 'zipcode' },
                      ]}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>

              {/* Location filter value row - only for System Managed (separate row) */}
              {studyType === 'purespectrum' && locationFilter !== 'none' && (
                <Row gutter={[24, 0]}>
                  {locationFilter === 'region' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('Region', 'region')}
                        name="region"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select Region"
                          options={demographicOptions.region}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('region')}
                          onChange={() => clearQuotaForField('region')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'division' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('Division', 'division')}
                        name="division"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select Division"
                          options={demographicOptions.division}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('division')}
                          onChange={() => clearQuotaForField('division')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'state' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('State', 'state')}
                        name="state"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select State"
                          options={demographicOptions.state}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('state')}
                          onChange={() => clearQuotaForField('state')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'dma' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('DMA', 'dma')}
                        name="dma"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select DMA"
                          options={demographicOptions.dma}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('dma')}
                          onChange={() => clearQuotaForField('dma')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'msa' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('MSA / CBSA', 'msa')}
                        name="msa"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select MSA / CBSA"
                          options={demographicOptions.msa}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('msa')}
                          onChange={() => clearQuotaForField('msa')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'csa' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('CSA', 'csa')}
                        name="csa"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select CSA"
                          options={demographicOptions.csa}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('csa')}
                          onChange={() => clearQuotaForField('csa')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'county' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota('County', 'county')}
                        name="county"
                      >
                        <XThemeSelect
                          className="fullWidth noRadius"
                          mode="multiple"
                          size="large"
                          placeholder="Select County"
                          options={demographicOptions.county}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          tabIndex={6}
                          disabled={isFieldDisabled('county')}
                          onChange={() => clearQuotaForField('county')}
                        />
                      </ThemeForm.Item>
                    </Col>
                  )}
                  {locationFilter === 'zipcode' && (
                    <Col xs={24} md={8}>
                      <ThemeForm.Item name="zipcode" noStyle>
                        <XThemeSelect mode="tags" style={{ display: 'none' }} />
                      </ThemeForm.Item>
                      <ThemeForm.Item
                        label={renderFieldLabelWithQuota(
                          <>Zipcode<Tooltip overlayStyle={{ maxWidth: 340 }} title={<div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 12, lineHeight: 1.6 }}><div style={{ fontWeight: 600, marginBottom: 4 }}>How to add Zipcodes:</div><div>1. Download the Zipcode Template.</div><div>2. Fill in your desired zipcodes (one per row).</div><div>3. Upload the completed .CSV file.</div><div>4. To start over, clear the upload and re-upload.</div><div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4 }}>Adding Zipcode Quotas:</div><div>After uploading, click "+ Quota" to set quota goals for individual zipcodes.</div></div>}><InfoCircleOutlined style={{ marginLeft: 4, fontSize: 12, color: 'rgba(10, 27, 57, 0.35)', cursor: 'help' }} /></Tooltip><span onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleZipcodeDownloadTemplate(); }} style={{ marginLeft: 6, fontFamily: "'Poppins', sans-serif", fontSize: 11, fontWeight: 500, color: '#3d2969', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}>Download Template</span></>,
                          'zipcode'
                        )}
                      >
                        <ZipcodeUploadArea>
                          <input
                            type="file"
                            accept=".csv"
                            ref={zipcodeFileInputRef}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleZipcodeCSVUpload(file);
                            }}
                          />
                          {watchedFields.zipcode?.length > 0 ? (
                            <>
                              <div className="zipcode-input-box has-file">
                                <FileTextOutlined className="file-icon" />
                                <span className="file-name">{zipcodeFileName || 'Loaded zipcodes'}</span>
                                <span className="file-count">{watchedFields.zipcode.length} zipcodes</span>
                                <button type="button" className="view-btn" onClick={() => setZipcodePreviewOpen(true)}>
                                  View
                                </button>
                                <CloseOutlined className="clear-btn" onClick={handleZipcodeClear} />
                              </div>
                              <ThemeModal
                                title={`Zipcodes (${watchedFields.zipcode.length})`}
                                open={zipcodePreviewOpen}
                                onCancel={() => setZipcodePreviewOpen(false)}
                                footer={null}
                                width={360}
                              >
                                <div className="zipcode-preview">
                                  {watchedFields.zipcode.map((z, i) => (
                                    <div key={i} className="preview-item">{z}</div>
                                  ))}
                                </div>
                              </ThemeModal>
                            </>
                          ) : (
                            <div
                              className="zipcode-input-box"
                              onClick={() => !isFieldDisabled('zipcode') && zipcodeFileInputRef.current?.click()}
                            >
                              <UploadOutlined className="upload-icon" />
                              <span className="upload-placeholder">Upload .CSV</span>
                            </div>
                          )}
                        </ZipcodeUploadArea>
                      </ThemeForm.Item>
                    </Col>
                  )}
                </Row>
              )}

              {/* Additional demographics and PS fields - only for System Managed studies */}
              {studyType === 'purespectrum' && (
              <Row gutter={[24, 0]}>
                {/* Row 3: Household Income | Children | Hispanic Origin (3 columns) */}
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Household Income Range', 'income')}
                  >
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <IncomeInputWrapper>
                          <span className="income-prefix">$</span>
                          <ThemeForm.Item
                            name="income_min"
                            noStyle
                            dependencies={['income_max']}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const maxValue = getFieldValue('income_max');
                                  if (value != null && maxValue != null && value > maxValue) {
                                    return Promise.reject(new Error('Min > Max'));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              placeholder="Min"
                              min={0}
                              max={999999}
                              formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                              parser={value => value.replace(/,/g, '')}
                              tabIndex={8}
                              disabled={isFieldDisabled('income_min')}
                              onChange={() => clearQuotaForField('income')}
                            />
                          </ThemeForm.Item>
                        </IncomeInputWrapper>
                      </div>
                      <span style={{ color: '#485066', fontSize: '12px', flexShrink: 0 }}>to</span>
                      <div style={{ flex: 1 }}>
                        <IncomeInputWrapper>
                          <span className="income-prefix">$</span>
                          <ThemeForm.Item
                            name="income_max"
                            noStyle
                            dependencies={['income_min']}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const minValue = getFieldValue('income_min');
                                  if (value != null && minValue != null && value < minValue) {
                                    return Promise.reject(new Error('Max < Min'));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              placeholder="Max"
                              min={0}
                              max={999999}
                              formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                              parser={value => value.replace(/,/g, '')}
                              tabIndex={9}
                              disabled={isFieldDisabled('income_max')}
                              onChange={() => clearQuotaForField('income')}
                            />
                          </ThemeForm.Item>
                        </IncomeInputWrapper>
                      </div>
                    </div>
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Children', 'children')}
                    name="children"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Children"
                      options={demographicOptions.children}
                      tabIndex={9}
                      disabled={isFieldDisabled('children')}
                      onChange={() => clearQuotaForField('children')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Hispanic Origin', 'hispanic_origin')}
                    name="hispanic_origin"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Hispanic Origin"
                      options={demographicOptions.hispanic_origin}
                      tabIndex={10}
                      disabled={isFieldDisabled('hispanic_origin')}
                      onChange={() => clearQuotaForField('hispanic_origin')}
                    />
                  </ThemeForm.Item>
                </Col>

              {/* Row 4: Education | Relationship Status | Device (3 columns) */}
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Education', 'education')}
                    name="education"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Education"
                      options={demographicOptions.education}
                      tabIndex={12}
                      disabled={isFieldDisabled('education')}
                      onChange={() => clearQuotaForField('education')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Relationship Status', 'relationship')}
                    name="relationship"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Relationship Status"
                      options={demographicOptions.relationship}
                      tabIndex={13}
                      disabled={isFieldDisabled('relationship')}
                      onChange={() => clearQuotaForField('relationship')}
                    />
                  </ThemeForm.Item>
                </Col>
                <Col xs={24} md={8}>
                  <ThemeForm.Item
                    label={renderFieldLabelWithQuota('Device', 'device')}
                    name="device"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Device"
                      options={demographicOptions.device}
                      tabIndex={11}
                      disabled={isFieldDisabled('device')}
                      onChange={() => clearQuotaForField('device')}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>
              )}

              {/* State/City + Race row (Employment only for System Managed) */}
              <Row gutter={[24, 0]}>
                {studyType === 'purespectrum' && (
                  <Col xs={24} md={12}>
                    <ThemeForm.Item
                      label={renderFieldLabelWithQuota('Employment', 'employment')}
                      name="employment"
                    >
                      <XThemeSelect
                        className="fullWidth noRadius"
                        mode="multiple"
                        size="large"
                        placeholder="Select Employment"
                        options={demographicOptions.employment}
                        tabIndex={14}
                        disabled={isFieldDisabled('employment')}
                        onChange={() => clearQuotaForField('employment')}
                      />
                    </ThemeForm.Item>
                  </Col>
                )}
                {/* State/City for Custom Upload only - same row as Race */}
                {studyType !== 'purespectrum' && locationFilter === 'state' && (
                  <Col xs={24} md={8}>
                    <ThemeForm.Item
                      label="State"
                      name="state"
                    >
                      <XThemeSelect
                        className="fullWidth noRadius"
                        mode="multiple"
                        size="large"
                        placeholder="Select State"
                        options={demographicOptions.state}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        tabIndex={6}
                        disabled={isFieldDisabled('state')}
                      />
                    </ThemeForm.Item>
                  </Col>
                )}
                {studyType !== 'purespectrum' && locationFilter === 'dma' && (
                  <Col xs={24} md={8}>
                    <ThemeForm.Item
                      label="DMA"
                      name="dma"
                    >
                      <XThemeSelect
                        className="fullWidth noRadius"
                        mode="multiple"
                        size="large"
                        placeholder="Select DMA"
                        options={demographicOptions.dma}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        tabIndex={6}
                        disabled={isFieldDisabled('dma')}
                      />
                    </ThemeForm.Item>
                  </Col>
                )}
                <Col xs={24} md={studyType === 'purespectrum' ? 12 : 8}>
                  <ThemeForm.Item
                    label={studyType === 'purespectrum' ? renderFieldLabelWithQuota('Race / Ethnicity', 'race') : 'Race / Ethnicity'}
                    name="race"
                  >
                    <XThemeSelect
                      className="fullWidth noRadius"
                      mode="multiple"
                      size="large"
                      placeholder="Select Race / Ethnicity"
                      options={demographicOptions.race}
                      tabIndex={15}
                      disabled={isFieldDisabled('race')}
                      onChange={() => clearQuotaForField('race')}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>


              {/* Advanced Targeting - only for System Managed */}
              {studyType === 'purespectrum' && (
                <AdvancedTargetingSection
                  advancedTargeting={advancedTargeting}
                  onChange={(newAT) => {
                    setAdvancedTargeting(newAT);
                    if (loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
                  }}
                  disabled={isEditDisabled}
                  quotas={quotas}
                  onOpenQuotaModal={handleOpenQuotaModal}
                  onDeleteQuota={handleDeleteQuota}
                  responsesValue={responsesValue}
                  isEditDisabled={isEditDisabled}
                />
              )}

              {/* Quota Nesting - only for System Managed */}
              {studyType === 'purespectrum' && (
                <NestedQuotaSection>
                  <span className="nq-title">Quota Nesting</span>

                  {nestedQuotas.length > 0 ? (() => {
                    const nest = nestedQuotas[0];
                    const fieldLabels = (nest.fields || []).map(f => {
                      const atMatch = f.match(/^at_(\d+)$/);
                      if (atMatch) {
                        const qualCode = parseInt(atMatch[1], 10);
                        const atItem = advancedTargeting.find(a => a.qualification_code === qualCode);
                        return atItem?.qualification_name || `AT ${qualCode}`;
                      }
                      const fieldLabelMap = { gender: 'Gender', age: 'Age', income: 'Income', race: 'Race', hispanic_origin: 'Hispanic Origin', relationship: 'Relationship', children: 'Children', employment: 'Employment', education: 'Education', device: 'Device', region: 'Region', division: 'Division', state: 'State', msa: 'MSA / CBSA', csa: 'CSA', county: 'County', dma: 'DMA', zipcode: 'Zipcode' };
                      return fieldLabelMap[f] || f;
                    });
                    const cellCount = (nest.cells || []).length;
                    const totalCount = (nest.cells || []).reduce((sum, c) => sum + (c.required_count || 0), 0);

                    return (
                      <>
                        <div className="nq-fields">
                          {fieldLabels.map((label, idx) => (
                            <span key={idx} className="nq-field-tag">{label}</span>
                          ))}
                        </div>
                        <span className="nq-summary">
                          {cellCount} cell{cellCount !== 1 ? 's' : ''} &middot; {totalCount} total
                        </span>
                        {!isEditDisabled && (
                          <div className="nq-actions">
                            <EditOutlined
                              className="nq-action-icon edit"
                              onClick={() => handleOpenNestedQuotaModal()}
                            />
                            <Popconfirm
                              title="Remove nested quota?"
                              description="This will remove all nested quota cells."
                              onConfirm={() => handleDeleteNestedQuota()}
                              okText="Remove"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true }}
                            >
                              <DeleteOutlined className="nq-action-icon delete" />
                            </Popconfirm>
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    !isEditDisabled && (
                      <button
                        type="button"
                        className="nq-add-btn"
                        disabled={!responsesValue || responsesValue <= 0 || quotas.length === 0}
                        onClick={() => {
                          if (responsesValue && responsesValue > 0 && quotas.length > 0) handleOpenNestedQuotaModal();
                        }}
                      >
                        <PlusOutlined style={{ marginRight: 4 }} /> Add Nested Quota
                      </button>
                    )
                  )}
                </NestedQuotaSection>
              )}

              {/* Feasibility Check - only for System Managed with check-feasibility permission */}
              {studyType === 'purespectrum' && canCheckFeasibility && (
                <>
                  <FeasibilitySection>
                    <UpdateButton
                      type="primary"
                      size="large"
                      onClick={handleCheckFeasibility}
                      loading={feasibilityLoading}
                      disabled={
                        isEditDisabled ||
                        !watchCampaignName ||
                        !watchResponses ||
                        !watchSurveyDuration ||
                        !watchDuration
                      }
                      style={{ minWidth: '200px' }}
                    >
                      {feasibilityLoading ? 'Checking...' : 'Check Feasibility'}
                    </UpdateButton>
                  </FeasibilitySection>

                  {feasibilityError && (
                    <FeasibilityResultCard className="error">
                      <div className="result-title">Feasibility Check Failed</div>
                      <div className="result-error">{feasibilityError}</div>
                    </FeasibilityResultCard>
                  )}

                  {feasibilityResult && (
                    <FeasibilityResultCard>
                      <div className="result-title">Feasibility Results</div>
                      <div className="result-grid">
                        <div className="result-item">
                          <span className="result-label">Total Feasibility</span>
                          <span className="result-value highlight">
                            {feasibilityResult.total_feasibility?.toLocaleString() ?? 'N/A'}
                          </span>
                        </div>
                        {canUseCPI && (
                        <div className="result-item">
                          <span className="result-label">Recommended CPI</span>
                          <span className="result-value">
                            {feasibilityResult.recommended_cpi != null
                              ? `$${feasibilityResult.recommended_cpi.toFixed(2)}`
                              : 'N/A'}
                          </span>
                          {feasibilityResult.recommended_cpi != null && !isFieldDisabled('cpi') && (
                            <Popconfirm
                              title="Apply Recommended CPI"
                              description={`This will update the CPI field to $${feasibilityResult.recommended_cpi.toFixed(2)}`}
                              onConfirm={() => {
                                form.setFieldsValue({ cpi: feasibilityResult.recommended_cpi });
                                notification.success({
                                  message: 'CPI Updated',
                                  description: `CPI set to $${feasibilityResult.recommended_cpi.toFixed(2)}`,
                                  duration: 2,
                                });
                              }}
                              okText="Apply"
                              cancelText="Cancel"
                            >
                              <span className="use-cpi-link">
                                <ArrowUpOutlined /> Apply to CPI
                              </span>
                            </Popconfirm>
                          )}
                        </div>
                        )}
                      </div>
                      {feasibilityStale && (
                        <div className="stale-warning" style={{ color: '#ff4d4f', fontSize: '13px', marginTop: '8px' }}>
                          Values have changed since last check. Please re-check feasibility.
                        </div>
                      )}
                    </FeasibilityResultCard>
                  )}
                </>
              )}
          </ThemeCard>
              {/* Survey Message - full width */}
              <Row gutter={[24, 0]}>
                <Col xs={24}>
                  <ThemeForm.Item
                    label="Study Message"
                    name="survey_message"
                    rules={[
                      {
                        validator: () => {
                          if (isFieldRequired('survey_message') && isSurveyMessageEmpty(surveyMessageRef.current)) {
                            return Promise.reject(new Error('Please enter study message'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Editor
                      apiKey={config.tiny_url_key}
                      initialValue={defaultMsg}
                      disabled={isFieldDisabled('survey_message')}
                      init={{
                        menubar: false,
                        height: 300,
                        width: '100%',
                        plugins: ['code', 'image', 'link', 'visualblocks'],
                        branding: false,
                        toolbar:
                          'undo redo | formatselect | ' +
                          'bold italic backcolor | alignleft aligncenter alignright alignjustify | blocks fontfamily fontsize | ' +
                          'bullist numlist outdent indent | image link |' +
                          'removeformat | visualblocks code | help ',
                        content_style:
                          'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                        font_size_formats: '8px 10px 12px 14px 18px 24px 36px',
                      }}
                      onInit={(evt, editor) => {
                        // Store editor instance ref
                        editorRef.current = editor;
                        if (isEdit && surveyMessageRef.current) {
                          // Edit mode: survey message loaded from API before editor init
                          editor.setContent(surveyMessageRef.current);
                        } else if (surveyMessageRef.current && surveyMessageRef.current !== defaultMsg) {
                          // Template loaded before editor init — sync editor with template content
                          editor.setContent(surveyMessageRef.current);
                        } else {
                          // Default: sync ref/state with editor's initialValue
                          const content = editor.getContent();
                          if (content) {
                            surveyMessageRef.current = content;
                            setSurveyMessage(content);
                            form.setFieldsValue({ survey_message: content });
                          }
                        }
                      }}
                      onEditorChange={(content) => {
                        setSurveyMessage(content);
                        surveyMessageRef.current = content;
                        // Set form field value so Ant Design validation works
                        form.setFieldsValue({ survey_message: content });
                        if (!isLoadingTemplateRef.current && loadedTemplateId && !formDirtyAfterLoad) setFormDirtyAfterLoad(true);
                      }}
                    />
                  </ThemeForm.Item>
                </Col>
              </Row>
            <ActionButtonsWrapper style={{ justifyContent: 'space-between' }}>
              {/* Save as Template - left side, only in create mode with permission */}
              {!isEdit && can('save-template') ? (
                <Tooltip title={!canSaveAsTemplate ? saveTemplateTooltip : ''}>
                  <span>
                    <CancelButton
                      size="large"
                      onClick={() => setSaveTemplateModalVisible(true)}
                      loading={saveCampaignTemplateState.apiState === 'loading'}
                      disabled={!canSaveAsTemplate}
                    >
                      Save as Template
                    </CancelButton>
                  </span>
                </Tooltip>
              ) : (
                <div />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CancelButton
                  size="large"
                  onClick={() => navigate(getBackUrl())}
                >
                  Cancel
                </CancelButton>
                <UpdateButton
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={
                    createCampaignState.apiState === 'loading' ||
                    updateCampaignState.apiState === 'loading'
                  }
                  disabled={isEditDisabled}
                >
                  {isEdit ? 'Update Study' : 'Create Study'}
                </UpdateButton>
              </div>
            </ActionButtonsWrapper>
          </ThemeForm>
        </FormContent>
        </Spin>
      </ThemeMainWrapper>

      {/* Quota Modal */}
      <QuotaModal
        visible={quotaModalVisible}
        onClose={handleCloseQuotaModal}
        onSave={handleSaveQuota}
        field={quotaModalField}
        selectedValues={(() => {
          if (!quotaModalField) return [];
          // AT fields: get condition_codes from advancedTargeting
          const atMatch = quotaModalField.match(/^at_(\d+)$/);
          if (atMatch) {
            const qualCode = parseInt(atMatch[1], 10);
            const atItem = advancedTargeting.find(a => a.qualification_code === qualCode);
            return atItem ? (atItem.condition_codes || []) : [];
          }
          return form.getFieldValue(quotaModalField) || [];
        })()}
        demographicOptions={(() => {
          if (!quotaModalField) return [];
          // AT fields: build options from condition_codes + selected_labels
          const atMatch = quotaModalField.match(/^at_(\d+)$/);
          if (atMatch) {
            const qualCode = parseInt(atMatch[1], 10);
            const atItem = advancedTargeting.find(a => a.qualification_code === qualCode);
            if (!atItem) return [];
            return (atItem.condition_codes || []).map((code, idx) => ({
              value: code,
              label: atItem.selected_labels?.[idx] || code,
              condition_code: code,
            }));
          }
          return demographicOptions[quotaModalField] || [];
        })()}
        existingQuota={quotaModalField ? getQuotaForField(quotaModalField) : null}
        totalResponses={form.getFieldValue('responses')}
        ageRange={quotaModalField === 'age' ? { min: ageSliderValue[0], max: ageSliderValue[1] } : null}
        incomeRange={quotaModalField === 'income' ? { min: form.getFieldValue('income_min'), max: form.getFieldValue('income_max') } : null}
        atQuestionName={quotaModalAtName}
      />

      {/* Nested Quota Modal */}
      <NestedQuotaModal
        visible={nestedQuotaModalVisible}
        onClose={handleCloseNestedQuotaModal}
        onSave={handleSaveNestedQuota}
        editingNest={editingNestIndex !== null ? nestedQuotas[editingNestIndex] : null}
        totalResponses={form.getFieldValue('responses')}
        formValues={{
          gender: form.getFieldValue('gender') || [],
          race: form.getFieldValue('race') || [],
          region: form.getFieldValue('region') || [],
          division: form.getFieldValue('division') || [],
          state: form.getFieldValue('state') || [],
          msa: form.getFieldValue('msa') || [],
          csa: form.getFieldValue('csa') || [],
          county: form.getFieldValue('county') || [],
          zipcode: form.getFieldValue('zipcode') || [],
          dma: form.getFieldValue('dma') || [],
          education: form.getFieldValue('education') || [],
          employment: form.getFieldValue('employment') || [],
          children: form.getFieldValue('children') || [],
          relationship: form.getFieldValue('relationship') || [],
          hispanic_origin: form.getFieldValue('hispanic_origin') || [],
          device: form.getFieldValue('device') || [],
        }}
        demographicOptions={demographicOptions}
        quotas={quotas}
        advancedTargeting={advancedTargeting}
        ageRange={{ min: ageSliderValue[0], max: ageSliderValue[1] }}
        incomeRange={{ min: form.getFieldValue('income_min'), max: form.getFieldValue('income_max') }}
      />

      {/* Save Template Modal */}
      <ThemeModal
        title="Save as Template"
        open={saveTemplateModalVisible}
        onCancel={() => {
          setSaveTemplateModalVisible(false);
          setTemplateName('');
        }}
        onOk={handleSaveAsTemplate}
        okText="Save Template"
        okButtonProps={{
          disabled: !templateName.trim(),
          loading: saveCampaignTemplateState.apiState === 'loading',
        }}
        width={480}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>
          <label>Template Name</label>
        </div>
        <ThemeInput
          size="large"
          placeholder="Enter template name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          maxLength={100}
          onPressEnter={() => {
            if (templateName.trim()) handleSaveAsTemplate();
          }}
        />
      </ThemeModal>
    </>
  );
};

const mapStateToProps = (state) => ({
  createCampaignState: state.campaign.createCampaignState,
  updateCampaignState: state.campaign.updateCampaignState,
  getCampaignByIdState: state.campaign.getCampaignByIdState,
  getPollingAgentsState: state.pollingAgent.getPollingAgentsState,
  getSurveyDetailState: state.survey.getSurveyDetailState,
  getCampaignTemplatesState: state.campaignTemplate.getCampaignTemplatesState,
  getCampaignTemplateByIdState: state.campaignTemplate.getCampaignTemplateByIdState,
  saveCampaignTemplateState: state.campaignTemplate.saveCampaignTemplateState,
});

const mapDispatchToProps = (dispatch) => ({
  createCampaign: (params) => dispatch(createCampaignApi(params)),
  createCampaignReset: () => dispatch(createCampaignReset()),
  updateCampaign: (id, params) => dispatch(updateCampaignApi(id, params)),
  updateCampaignReset: () => dispatch(updateCampaignReset()),
  getCampaignById: (id) => dispatch(getCampaignByIdApi(id)),
  getCampaignByIdReset: () => dispatch(getCampaignByIdReset()),
  getPollingAgents: (params) => dispatch(getPollingAgentsApi(params)),
  getPollingAgentsReset: () => dispatch(getPollingAgentsReset()),
  getSurveyDetail: (surveyId) => dispatch(getSurveyDetailApi(surveyId)),
  getSurveyDetailReset: () => dispatch(getSurveyDetailReset()),
  getCampaignTemplates: () => dispatch(getCampaignTemplatesApi()),
  getCampaignTemplatesReset: () => dispatch(getCampaignTemplatesReset()),
  getCampaignTemplateById: (id) => dispatch(getCampaignTemplateByIdApi(id)),
  getCampaignTemplateByIdReset: () => dispatch(getCampaignTemplateByIdReset()),
  saveCampaignTemplate: (data) => dispatch(saveCampaignTemplateApi(data)),
  saveCampaignTemplateReset: () => dispatch(saveCampaignTemplateReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateCampaign);
